import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import '../config/api'; // Ensure axios interceptors are configured
import { parseAmountShorthand, replaceShorthandOnBlur } from '../utils/amountUtils';
import { getTenantToday } from '../utils/dateUtils';
import { sanitizeMarkdown } from '../utils/sanitize';
import LoadingOverlay from './shared/LoadingOverlay';
import { useNavigate } from 'react-router-dom';
import {
  Bot,
  Sparkles,
  Send,
  Search,
  CheckCircle2,
  AlertCircle,
  PlusCircle,
  Trash2,
  TrendingUp,
  Activity,
  Layers,
  DollarSign,
  Plus,
  ExternalLink,
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Info
} from 'lucide-react';
import { cn } from '../lib/utils';

import { DateField } from './shared/DateField';
import { useCloLane, money, parShort, showValue, stateText, rowResult } from './construction/useCloLane';

// ==================== ANIMATIONS ====================

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

// ==================== STYLED COMPONENTS ====================

const PageContainer = styled.div`
  min-height: calc(100vh - 60px);
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  padding: 32px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const PageHeader = styled.div`
  max-width: 1400px;
  margin: 0 auto 32px;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #f1f5f9;
  margin: 0 0 8px 0;
  background: linear-gradient(135deg, #ffffff 0%, #94a3b8 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const PageSubtitle = styled.p`
  color: #94a3b8;
  font-size: 1.125rem;
  margin: 0;
`;

// Main Content Grid
const ContentGrid = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 380px 1fr;
  gap: 24px;
  animation: ${slideUp} 0.5s ease-out;
`;

// Builder Panel (Left)
const BuilderPanel = styled.div`
  background: rgba(30, 41, 59, 0.6);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  border: 1px solid rgba(148, 163, 184, 0.1);
  overflow: hidden;
`;

const BuilderSection = styled.div`
  padding: 24px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.1);
  
  &:last-child {
    border-bottom: none;
  }
`;

const SectionNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
  color: white;
  border-radius: 50%;
  font-size: 0.875rem;
  font-weight: 700;
  margin-right: 12px;
`;

const SectionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #f1f5f9;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
`;

// Chip Selection
const ChipGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Chip = styled.button<{ selected?: boolean; color?: string }>`
  padding: 8px 16px;
  border-radius: 100px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid ${props => props.selected ? (props.color || '#3b82f6') : 'rgba(148, 163, 184, 0.3)'};
  background: ${props => props.selected ? (props.color || '#3b82f6') + '20' : 'rgba(30, 41, 59, 0.6)'};
  color: ${props => props.selected ? (props.color || '#60a5fa') : '#94a3b8'};
  
  &:hover {
    border-color: ${props => props.color || '#3b82f6'};
    transform: translateY(-1px);
  }
`;

// Security Card
const SecurityCard = styled.div`
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 12px;
  position: relative;
  
  &:hover {
    border-color: #3b82f6;
  }
`;

const SecurityHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const SecurityCusip = styled.div`
  font-family: 'JetBrains Mono', monospace;
  font-size: 1.125rem;
  font-weight: 700;
  color: #60a5fa;
`;

const SecurityName = styled.div`
  font-size: 0.875rem;
  color: #94a3b8;
  margin-top: 4px;
`;

const SecurityMeta = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
`;

const MetaItem = styled.div`
  text-align: center;
  padding: 8px;
  background: rgba(15, 23, 42, 0.6);
  border-radius: 8px;
`;

const MetaLabel = styled.div`
  font-size: 0.625rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  margin-bottom: 2px;
`;

const MetaValue = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #e2e8f0;
`;

const RemoveBtn = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #ff4757;
  border: none;
  color: white;
  cursor: pointer;
  font-size: 0.75rem;
  opacity: 0.8;
  transition: all 0.2s;
  
  &:hover {
    opacity: 1;
    transform: scale(1.1);
  }
`;

const AddSecurityBtn = styled.button`
  width: 100%;
  padding: 16px;
  border: 2px dashed rgba(148, 163, 184, 0.3);
  border-radius: 16px;
  background: transparent;
  color: #64748b;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    border-color: #667eea;
    color: #667eea;
    background: rgba(59, 130, 246, 0.1);
  }
`;

// Portfolio Selection
const PortfolioGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
`;

const PortfolioChip = styled.button<{ selected?: boolean }>`
  padding: 12px;
  border-radius: 12px;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
  border: 2px solid ${props => props.selected ? '#667eea' : 'rgba(148, 163, 184, 0.1)'};
  background: ${props => props.selected ? '#667eea' : 'rgba(30, 41, 59, 0.6)'};
  color: ${props => props.selected ? 'white' : '#94a3b8'};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  }
`;

// Input Field
const InputGroup = styled.div`
  margin-bottom: 16px;
`;

const InputLabel = styled.label`
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid rgba(148, 163, 184, 0.2);
  border-radius: 12px;
  font-size: 0.9375rem;
  background: rgba(15, 23, 42, 0.6);
  color: #e2e8f0;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
  }
`;

const StyledSelect = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid rgba(148, 163, 184, 0.2);
  border-radius: 12px;
  font-size: 0.9375rem;
  background: rgba(30, 41, 59, 0.6);
  color: #e2e8f0;
  cursor: pointer;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

// Execute Button
const ExecuteButton = styled.button<{ $loading?: boolean }>`
  width: 100%;
  padding: 18px 24px;
  border: none;
  border-radius: 16px;
  font-size: 1.125rem;
  font-weight: 700;
  cursor: pointer;
  background: ${props => props.$loading 
    ? 'linear-gradient(90deg, #667eea, #764ba2, #667eea)' 
    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  background-size: ${props => props.$loading ? '200% 100%' : '100% 100%'};
  animation: ${props => props.$loading ? shimmer : 'none'} 1.5s infinite;
  color: white;
  box-shadow: 0 8px 30px rgba(102, 126, 234, 0.4);
  transition: all 0.3s;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px rgba(102, 126, 234, 0.5);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

// Results Panel (Right)
const ResultsPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ResultCard = styled.div`
  background: rgba(30, 41, 59, 0.6);
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.15);
  overflow: hidden;
  animation: ${slideUp} 0.5s ease-out;
`;

const ResultHeader = styled.div`
  padding: 24px;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  color: white;
`;

const ResultTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0 0 4px 0;
`;

const ResultSubtitle = styled.p`
  font-size: 0.875rem;
  opacity: 0.7;
  margin: 0;
`;

// Impact Metrics
const ImpactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1px;
  background: rgba(15, 23, 42, 0.3);
`;

const ImpactCell = styled.div`
  background: rgba(30, 41, 59, 0.6);
  padding: 24px 20px;
  text-align: center;
`;

const ImpactValue = styled.div<{ positive?: boolean; negative?: boolean }>`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${props => props.positive ? '#00c853' : props.negative ? '#ff4757' : '#f1f5f9'};
  margin-bottom: 4px;
  font-family: 'IBM Plex Mono', monospace;
`;

const ImpactLabel = styled.div`
  font-size: 0.75rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

// Comparison Table
const ComparisonContainer = styled.div`
  padding: 24px;
`;

const ComparisonRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1.5fr 1fr;
  gap: 16px;
  padding: 16px 0;
  border-bottom: 1px solid rgba(148, 163, 184, 0.2);
  align-items: center;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: rgba(102, 126, 234, 0.05);
  }
`;

const ComparisonHeader = styled(ComparisonRow)`
  font-size: 0.7rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 2px solid rgba(255, 255, 255, 0.1);
`;

const PortfolioName = styled.div`
  font-weight: 600;
  color: #e2e8f0;
`;

const DurationValue = styled.div`
  text-align: right;
  font-family: 'IBM Plex Mono', monospace;
  font-size: 0.9375rem;
`;

const ChangeValue = styled.div<{ positive?: boolean }>`
  text-align: right;
  font-family: 'IBM Plex Mono', monospace;
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${props => props.positive ? '#00c853' : '#ff4757'};
  background: ${props => props.positive ? '#00c85310' : '#ff475710'};
  padding: 4px 8px;
  border-radius: 6px;
  display: inline-block;
`;

const MarketValue = styled.div`
  text-align: right;
  font-family: 'IBM Plex Mono', monospace;
  font-size: 0.9375rem;
  color: #94a3b8;
`;

const AllocatedAmount = styled.div`
  text-align: right;
  font-family: 'IBM Plex Mono', monospace;
  font-size: 1rem;
  font-weight: 700;
  color: #667eea;
  background: rgba(102, 126, 234, 0.15);
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid rgba(102, 126, 234, 0.3);
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 12px;
  padding: 24px;
  background: rgba(15, 23, 42, 0.4);
  border-top: 1px solid rgba(255, 255, 255, 0.05);
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: ${props => props.variant === 'primary' ? 'none' : '1px solid rgba(255, 255, 255, 0.1)'};
  background: ${props => props.variant === 'primary' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'rgba(30, 41, 59, 0.6)'};
  color: white;
  
  &:hover {
    transform: translateY(-1px);
    background: ${props => props.variant === 'primary' ? 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)' : 'rgba(30, 41, 59, 0.8)'};
  }
`;

const ErrorBanner = styled.div`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 16px;
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #f87171;
  font-weight: 500;
  backdrop-filter: blur(10px);
`;

// Empty State
const EmptyResults = styled.div`
  background: rgba(30, 41, 59, 0.6);
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.15);
  padding: 80px 40px;
  text-align: center;
  border: 2px dashed rgba(148, 163, 184, 0.2);
`;


const EmptyTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #f1f5f9;
  margin: 0 0 8px 0;
`;

const EmptyText = styled.p`
  color: #64748b;
  font-size: 1rem;
  margin: 0;
  max-width: 400px;
  margin: 0 auto;
`;

// Modal
const ModalBackdrop = styled.div<{ show: boolean }>`
  display: ${props => props.show ? 'flex' : 'none'};
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalCard = styled.div`
  background: #0f172a;
  border-radius: 24px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 25px 80px rgba(0,0,0,0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  animation: ${slideUp} 0.3s ease-out;
`;

const ModalHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: #e2e8f0;
  margin: 0;
`;

const ModalClose = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: rgba(15, 23, 42, 0.4);
  color: #94a3b8;
  font-size: 1.25rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #ff4757;
    color: white;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
  max-height: 60vh;
  overflow-y: auto;
`;

const ModalFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid rgba(148, 163, 184, 0.2);
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  background: rgba(15, 23, 42, 0.4);
`;

const ModalButton = styled.button<{ primary?: boolean }>`
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: ${props => props.primary ? 'none' : '2px solid rgba(148, 163, 184, 0.2)'};
  background: ${props => props.primary ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'rgba(30, 41, 59, 0.6)'};
  color: ${props => props.primary ? 'white' : '#94a3b8'};
  
  &:hover {
    transform: translateY(-1px);
    ${props => !props.primary && 'border-color: #667eea; color: #667eea;'}
  }
`;

// Target Input Table
const TargetTable = styled.div`
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 16px;
  overflow: hidden;
`;

const TargetRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  border-bottom: 1px solid rgba(148, 163, 184, 0.1);
  
  &:last-child {
    border-bottom: none;
  }
`;

const TargetCell = styled.div`
  padding: 16px;
  display: flex;
  align-items: center;
  color: #e2e8f0;
  
  &:first-child {
    font-weight: 500;
    background: rgba(15, 23, 42, 0.4);
  }
`;

const TargetInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 8px;
  font-size: 0.9375rem;
  font-family: 'IBM Plex Mono', monospace;
  text-align: right;
  background: rgba(15, 23, 42, 0.6);
  color: #e2e8f0;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

// AI Chat Styled Components




const AIChatMessages = styled.div`
  max-height: 200px;
  overflow-y: auto;
  margin-bottom: 12px;
`;

const AIChatMessage = styled.div<{ role: string }>`
  padding: 10px 14px;
  border-radius: 12px;
  margin-bottom: 8px;
  max-width: 85%;
  font-size: 0.875rem;
  line-height: 1.4;
  
  ${props => props.role === 'user' ? `
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    margin-left: auto;
  ` : `
    background: rgba(15, 23, 42, 0.4);
    color: #e2e8f0;
    margin-right: auto;
  `}
`;



const AIButton = styled.button<{ variant?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  
  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
    }
  ` : `
    background: rgba(15, 23, 42, 0.3);
    color: #94a3b8;
    
    &:hover {
      background: rgba(15, 23, 42, 0.5);
      color: white;
    }
  `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const AISuggestionCard = styled.div`
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
  border: 1px solid rgba(102, 126, 234, 0.3);
  border-radius: 16px;
  padding: 16px;
  margin-top: 12px;
`;

const AISuggestionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const AISuggestionTitle = styled.h4`
  margin: 0;
  color: #667eea;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 6px;
`;




// One box for instructions and questions alike.
const CommandBar = styled.div`
  max-width: 1400px;
  margin: 0 auto 24px;
  padding: 18px 22px;
  border-radius: 20px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.12) 0%, rgba(30, 41, 59, 0.72) 55%, rgba(139, 92, 246, 0.10) 100%);
  border: 1px solid rgba(129, 140, 248, 0.28);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px);
  animation: ${slideUp} 0.4s ease-out;
`;

const CommandInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 14px 16px;
  border-radius: 14px;
  font-size: 1rem;
  background: rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(148, 163, 184, 0.2);
  color: #f1f5f9;

  &:focus {
    outline: none;
    border-color: #818cf8;
    box-shadow: 0 0 0 4px rgba(129, 140, 248, 0.15);
  }

  &::placeholder {
    color: #64748b;
  }
`;

const CommandHint = styled.div`
  margin-top: 8px;
  font-size: 0.75rem;
  color: #94a3b8;
`;

const ExampleChip = styled.button`
  text-align: left;
  font-size: 0.75rem;
  color: #c7d2fe;
  background: rgba(99, 102, 241, 0.08);
  border: 1px solid rgba(129, 140, 248, 0.25);
  border-radius: 999px;
  padding: 6px 12px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: rgba(99, 102, 241, 0.18);
    color: #ffffff;
  }
`;

/** A green tick beside a section title once that section has what the analysis needs. */
const Tick = ({ on }: { on: boolean }) => (on ? <CheckCircle2 className="w-4 h-4 text-emerald-400 ml-2" /> : null);

// ==================== CONSTRUCTION MODE TOGGLE ====================

const ModeToggleContainer = styled.div`
  display: flex;
  gap: 0;
  background: rgba(15, 23, 42, 0.6);
  border-radius: 18px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  padding: 4px;
  margin-bottom: 24px;
  max-width: 420px;
`;

const ModeToggleButton = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 12px 20px;
  border-radius: 14px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid ${({ $active }) => ($active ? 'rgba(96, 165, 250, 0.55)' : 'transparent')};
  background: ${({ $active }) => ($active
    ? 'linear-gradient(135deg, rgba(29, 78, 216, 0.34) 0%, rgba(14, 116, 144, 0.22) 100%)'
    : 'transparent')};
  color: ${({ $active }) => ($active ? '#f8fafc' : '#64748b')};
  box-shadow: ${({ $active }) => ($active ? '0 8px 24px rgba(37, 99, 235, 0.18)' : 'none')};

  &:hover {
    color: #f8fafc;
    background: ${({ $active }) => ($active
      ? 'linear-gradient(135deg, rgba(29, 78, 216, 0.38) 0%, rgba(14, 116, 144, 0.24) 100%)'
      : 'rgba(30, 41, 59, 0.6)')};
  }
`;

// ==================== ALTERNATIVES PLANNING STYLED COMPONENTS ====================

const AlternativesContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  animation: ${slideUp} 0.5s ease-out;
`;

const SleeveGrid = styled.div`
  background: rgba(30, 41, 59, 0.6);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  border: 1px solid rgba(148, 163, 184, 0.1);
  overflow: hidden;
`;

const SleeveGridHeader = styled.div`
  padding: 24px;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border-bottom: 1px solid rgba(148, 163, 184, 0.1);
`;

const SleeveGridTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: #f8fafc;
  margin: 0 0 4px 0;
`;

const SleeveGridSubtitle = styled.p`
  font-size: 0.875rem;
  color: #94a3b8;
  margin: 0;
`;

const SleeveTableContainer = styled.div`
  padding: 0;
`;

const SleeveTableRow = styled.div<{ $isHeader?: boolean }>`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 2.5fr;
  gap: 16px;
  padding: 16px 24px;
  border-bottom: 1px solid rgba(148, 163, 184, ${({ $isHeader }) => $isHeader ? '0.2' : '0.08'});
  align-items: center;
  cursor: ${({ $isHeader }) => $isHeader ? 'default' : 'pointer'};
  transition: background 0.15s ease;

  ${({ $isHeader }) => !$isHeader && `
    &:hover {
      background: rgba(59, 130, 246, 0.04);
    }
  `}

  &:last-child {
    border-bottom: none;
  }
`;

const SleeveTableHeaderCell = styled.div`
  font-size: 0.7rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const SleeveNameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SleeveColorDot = styled.div<{ color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ color }) => color};
  flex-shrink: 0;
`;

const SleeveName = styled.span`
  font-weight: 600;
  color: #e2e8f0;
  font-size: 0.9rem;
`;

const SleeveTargetInput = styled.input`
  width: 80px;
  padding: 6px 10px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 8px;
  font-size: 0.875rem;
  font-family: 'IBM Plex Mono', monospace;
  text-align: right;
  background: rgba(15, 23, 42, 0.6);
  color: #e2e8f0;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const SleevePercentValue = styled.div`
  font-family: 'IBM Plex Mono', monospace;
  font-size: 0.9rem;
  font-weight: 600;
  color: #94a3b8;
  text-align: right;
`;

const DriftBadge = styled.span<{ $severity: 'green' | 'amber' | 'red' }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  font-family: 'IBM Plex Mono', monospace;
  background: ${({ $severity }) =>
    $severity === 'green' ? 'rgba(16, 185, 129, 0.1)' :
    $severity === 'amber' ? 'rgba(245, 158, 11, 0.1)' :
    'rgba(239, 68, 68, 0.1)'};
  color: ${({ $severity }) =>
    $severity === 'green' ? '#10b981' :
    $severity === 'amber' ? '#f59e0b' :
    '#ef4444'};
  border: 1px solid ${({ $severity }) =>
    $severity === 'green' ? 'rgba(16, 185, 129, 0.2)' :
    $severity === 'amber' ? 'rgba(245, 158, 11, 0.2)' :
    'rgba(239, 68, 68, 0.2)'};
`;

const RecommendedAction = styled.div`
  font-size: 0.82rem;
  line-height: 1.4;
  color: #cbd5e1;
`;

const ActionLink = styled.span`
  color: #60a5fa;
  cursor: pointer;
  font-weight: 500;
  text-decoration: underline;
  text-decoration-color: rgba(96, 165, 250, 0.3);
  transition: all 0.15s ease;

  &:hover {
    color: #93c5fd;
    text-decoration-color: rgba(96, 165, 250, 0.6);
  }
`;

const CommitmentCard = styled.div`
  background: rgba(15, 23, 42, 0.8);
  border: 1px solid rgba(139, 92, 246, 0.3);
  border-radius: 16px;
  padding: 20px;
  margin-top: 16px;
  animation: ${slideUp} 0.3s ease-out;
`;

const CommitmentCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
`;

const CommitmentCardTitle = styled.h4`
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #c4b5fd;
`;

const CommitmentMetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
`;

const CommitmentMetric = styled.div`
  padding: 12px;
  background: rgba(30, 41, 59, 0.6);
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.08);
  text-align: center;
`;

const CommitmentMetricLabel = styled.div`
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  margin-bottom: 4px;
`;

const CommitmentMetricValue = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: #f1f5f9;
  font-family: 'IBM Plex Mono', monospace;
`;

const TimelineContainer = styled.div`
  background: rgba(15, 23, 42, 0.8);
  border: 1px solid #334155;
  border-radius: 10px;
  padding: 20px;
  margin-top: 20px;
`;

const TimelineTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: #f8fafc;
  margin: 0 0 4px 0;
`;

const TimelineSubtitle = styled.p`
  font-size: 0.82rem;
  color: #94a3b8;
  margin: 0 0 20px 0;
`;

const TimelineGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 8px;
`;

const TimelineMonth = styled.div`
  text-align: center;
`;

const TimelineMonthLabel = styled.div`
  font-size: 0.65rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
`;

const TimelineBarContainer = styled.div`
  height: 120px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 2px;
  position: relative;
`;

const TimelineBar = styled.div<{ $height: number; $type: 'call' | 'distribution' }>`
  width: 100%;
  max-width: 28px;
  height: ${({ $height }) => Math.max($height, 4)}px;
  border-radius: 4px;
  background: ${({ $type }) => $type === 'call'
    ? 'linear-gradient(180deg, #ef4444, #dc2626)'
    : 'linear-gradient(180deg, #10b981, #059669)'};
  opacity: 0.85;
  transition: opacity 0.15s;

  &:hover {
    opacity: 1;
  }
`;

const TimelineNetLabel = styled.div<{ $positive: boolean }>`
  font-size: 0.6rem;
  font-weight: 600;
  font-family: 'IBM Plex Mono', monospace;
  color: ${({ $positive }) => $positive ? '#10b981' : '#ef4444'};
  margin-top: 4px;
`;

const SleeveTotalRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 2.5fr;
  gap: 16px;
  padding: 16px 24px;
  background: rgba(15, 23, 42, 0.4);
  border-top: 2px solid rgba(148, 163, 184, 0.15);
  font-weight: 700;
`;

const LiquidityWarningBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.3);
  color: #f59e0b;
  margin-top: 8px;
`;

// ==================== SLEEVE BUCKET DATA ====================

const SLEEVE_BUCKETS = [
  { key: 'publicFI', label: 'Public Fixed Income', color: '#3b82f6', targetDefault: 30 },
  { key: 'publicEquity', label: 'Public Equity', color: '#10b981', targetDefault: 20 },
  { key: 'privateCredit', label: 'Private Credit', color: '#8b5cf6', targetDefault: 15 },
  { key: 'reEquity', label: 'RE Equity', color: '#f59e0b', targetDefault: 10 },
  { key: 'reDebt', label: 'RE Debt', color: '#ef4444', targetDefault: 5 },
  { key: 'peVc', label: 'PE / VC', color: '#06b6d4', targetDefault: 10 },
  { key: 'hedgeFunds', label: 'Hedge Funds', color: '#ec4899', targetDefault: 5 },
  { key: 'cashReserve', label: 'Cash Reserve', color: '#64748b', targetDefault: 5 },
];

// Maps security asset class / type into a sleeve bucket key.
// Must handle both dim_asset_class names (e.g. "Fixed Income") and
// security_type fallbacks (e.g. "Senior Secured Loan", "RE_DEBT", "PRIVATE_CREDIT").
const mapToSleeveBucket = (assetClassName?: string, securityType?: string): string => {
  const ac = (assetClassName || '').toLowerCase();
  const st = (securityType || '').toLowerCase();
  const combined = ac + ' ' + st;

  // Cash
  if (combined.includes('cash') || combined.includes('money market'))
    return 'cashReserve';

  // RE Debt (check before generic RE / loan)
  if (ac.includes('re_debt') || ac.includes('re debt') || st.includes('re_debt')
      || (combined.includes('real estate') && combined.includes('debt'))
      || combined.includes('cmbs'))
    return 'reDebt';

  // RE Equity / REIT
  if (ac.includes('real_estate_equity') || ac.includes('re equity') || ac.includes('real estate equity')
      || st.includes('real_estate_equity') || st.includes('reit')
      || ac.includes('real_estate_fund') || st.includes('real_estate_fund'))
    return 'reEquity';
  if (combined.includes('real estate') || combined.includes('real_estate'))
    return 'reEquity';

  // PE / VC
  if (ac.includes('pe_commitment') || ac.includes('private equity') || ac.includes('pe/')
      || st.includes('pe_commitment') || combined.includes('venture'))
    return 'peVc';

  // CLO — bucket under private credit
  if (combined.includes('clo') || combined.includes('structured credit'))
    return 'privateCredit';

  // Private Credit / Leveraged Loans
  if (ac.includes('private_credit') || ac.includes('private credit')
      || st.includes('private_credit') || combined.includes('term_loan')
      || combined.includes('senior secured loan') || combined.includes('second lien')
      || combined.includes('leveraged') || combined.includes('loan'))
    return 'privateCredit';

  // Hedge Funds
  if (combined.includes('hedge'))
    return 'hedgeFunds';

  // Public Equity
  if ((ac.includes('equity') || st.includes('equity')) && !combined.includes('clo'))
    return 'publicEquity';

  // Senior Secured / Senior Unsecured / Senior Subordinated bonds
  if (combined.includes('senior secured') || combined.includes('senior unsecured')
      || combined.includes('senior subordinated') || combined.includes('high yield'))
    return 'publicFI';

  // Public Fixed Income (default for bonds, treasuries, etc.)
  if (combined.includes('fixed income') || combined.includes('bond')
      || combined.includes('treasury') || combined.includes('mbs')
      || combined.includes('abs') || combined.includes('agency')
      || combined.includes('municipal'))
    return 'publicFI';

  return 'publicFI'; // default
};

// Navigation routes for each sleeve bucket
const SLEEVE_ROUTES: Record<string, string> = {
  publicFI: '/trade-entry',
  publicEquity: '/trade-entry',
  privateCredit: '/trade-entry/private-fixed-income',
  reEquity: '/alternatives/re-equity',
  reDebt: '/alternatives/re-debt',
  peVc: '/alternatives/pe',
  hedgeFunds: '/trade-entry',
  cashReserve: '/trade-entry',
};

// Checks if a security is a private/illiquid asset
const isPrivateAsset = (security: any): boolean => {
  const rawAc = security?.assetClassName || security?.assetClass?.assetClassName || (typeof security?.assetClass === 'string' ? security.assetClass : '');
  const ac = (rawAc || '').toLowerCase();
  const st = (security?.securityType || '').toLowerCase();
  return ac.includes('private') || ac.includes('real estate') || ac.includes('hedge') ||
    st.includes('private') || st.includes('loan') || st.includes('real estate') ||
    st.includes('pe') || st.includes('vc') || st.includes('hedge');
};

// ==================== MAIN COMPONENT ====================

type TargetModeType = 'percent' | 'duration' | 'spread' | 'amount';

/** A leveraged loan takes the managed-CLO lane: loan economics, indenture tests, independent tickets. */
const isLeveragedLoan = (security: any): boolean => {
  const st = String(security?.securityType || '').toUpperCase();
  const ac = String(security?.assetClassName || security?.assetClass?.assetClassName || (typeof security?.assetClass === 'string' ? security.assetClass : '')).toUpperCase();
  return st.includes('LEVERAGED_LOAN') || st === 'TERM_LOAN' || st === 'SENIOR SECURED LOAN' || ac.includes('LEVERAGED') || ac.includes('LOAN');
};

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

const MultiPortfolioConstructionScreen: React.FC = () => {
  const navigate = useNavigate();

  // Construction Mode
  const [constructionMode, setConstructionMode] = useState<'liquid' | 'alternatives'>('liquid');

  // Alternatives Planning State
  const [sleeveTargets, setSleeveTargets] = useState<Record<string, number>>(() => {
    const defaults: Record<string, number> = {};
    SLEEVE_BUCKETS.forEach(b => { defaults[b.key] = b.targetDefault; });
    return defaults;
  });
  const [selectedSleeve, setSelectedSleeve] = useState<string | null>(null);
  const [hoveredSleeve, setHoveredSleeve] = useState<string | null>(null);

  // Trade Configuration
  const [direction, setDirection] = useState<'buy' | 'sell'>('buy');
  const [targetMode, setTargetMode] = useState<TargetModeType>('percent');
  const [asOfDate, setAsOfDate] = useState(getTenantToday());
  
  // Securities
  const [selectedSecurities, setSelectedSecurities] = useState<any[]>([]);
  const [showSecurityModal, setShowSecurityModal] = useState(false);
  const [searchCusip, setSearchCusip] = useState('');
  
  // Portfolios
  const [portfolios, setPortfolios] = useState<any[]>([]);
  const [portfolioGroups, setPortfolioGroups] = useState<any[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [selectionMode, setSelectionMode] = useState<'all' | 'group' | 'individual' | 'clo'>('all');
  const [selectedPortfolioIds, setSelectedPortfolioIds] = useState<number[]>([]);
  const [showGroupManager, setShowGroupManager] = useState(false);
  const [editingGroup, setEditingGroup] = useState<any>(null);
  const [groupForm, setGroupForm] = useState({ groupName: '', description: '' });
  
  // Targets
  const [targets, setTargets] = useState<{[key: string]: string}>({});
  const [showTargetModal, setShowTargetModal] = useState(false);
  const [totalAmountFromNLP, setTotalAmountFromNLP] = useState<number | null>(null);
  
  // Results
  const [results, setResults] = useState<any>(null);
  // Close duration gap: the engine's sizing of a bond across the funds short of their target duration.
  const [durationGap, setDurationGap] = useState<any>(null);
  const [createdOrders, setCreatedOrders] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // AI Features
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const cleanPriceRef = useRef<HTMLInputElement>(null);
  const [chatLoading, setChatLoading] = useState(false);
  const [nlInput, setNlInput] = useState('');
  const [nlLoading, setNlLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState<any>(null);
  // Provenance: the NL command that shaped the current setup, or null when the
  // user built it manually. Tickets must not claim AI assistance that never
  // happened (F-TB-022).
  const [appliedIntentText, setAppliedIntentText] = useState<string | null>(null);
  // Deep link from a CLO card: ?portfolioId= preselects that managed CLO once a loan is chosen.
  const cloDeepLink = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('portfolioId');
    const groupId = params.get('groupId');
    return { portfolioId: id ? Number(id) : null, groupId: groupId ? Number(groupId) : null, managedClo: params.get('clo') === '1' || !!id || !!groupId };
  }, []);
  const loanSecurity = selectedSecurities.length > 0 && isLeveragedLoan(selectedSecurities[0]) ? selectedSecurities[0] : null;
  // The managed-CLO lane: a leveraged loan is sized into managed CLOs against their indenture
  // rules and booked as one LL block with an allocation per CLO (design §3.5).
  const lane = useCloLane(loanSecurity ? { id: loanSecurity.id, cusip: loanSecurity.cusip, securityName: loanSecurity.securityName, currentPrice: loanSecurity.currentPrice ?? loanSecurity.price ?? null } : null, cloDeepLink.portfolioId);
  const [cloMatrixOpen, setCloMatrixOpen] = useState<Record<number, boolean>>({});
  // Per-CLO detail is collapsed by default; a CLO with a failing or open test stays open.
  const [cloDetailOpen, setCloDetailOpen] = useState<Record<number, boolean>>({});
  const [securitySuggestions, setSecuritySuggestions] = useState<any[]>([]);
  const [pendingLoanIntent, setPendingLoanIntent] = useState<any>(null);
  const [groupNameDraft, setGroupNameDraft] = useState('');
  // Apply Intent on a loan: the lane takes the terms once its managed CLOs are loaded.
  useEffect(() => {
    if (!loanSecurity || !pendingLoanIntent || lane.managed.length === 0) return;
    // A named group drives the selection; otherwise the managed CLOs named in the command.
    const namedGroup = pendingLoanIntent.portfolioGroupId ? portfolioGroups.find(g => g.id === pendingLoanIntent.portfolioGroupId) : null;
    if (namedGroup) { setSelectionMode('group'); setSelectedGroupId(namedGroup.id); lane.selectGroup(namedGroup); }
    else setSelectionMode('clo');
    lane.applyIntent(pendingLoanIntent); setPendingLoanIntent(null);
  }, [loanSecurity?.id, pendingLoanIntent, lane.managed.length]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!loanSecurity || !cloDeepLink.groupId || lane.managed.length === 0) return;
    const group = portfolioGroups.find(g => g.id === cloDeepLink.groupId);
    if (group) { setSelectionMode('group'); setSelectedGroupId(group.id); lane.selectGroup(group); }
  }, [loanSecurity?.id, lane.managed.length, portfolioGroups.length]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (loanSecurity) setSelectionMode('clo'); else if (selectionMode === 'clo') setSelectionMode('all'); }, [loanSecurity?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const amountBlur = (key: any, setter: (k: any, v: string) => void) => (e: React.FocusEvent<HTMLInputElement>) => setter(key, replaceShorthandOnBlur(e.target.value));
  const [aiSuggestLoading, setAiSuggestLoading] = useState(false);
  
  const [blockingOperation, setBlockingOperation] = useState<{
    isActive: boolean;
    message: string;
    subMessage?: string;
  }>({ isActive: false, message: '' });

  // Fetch data — portfolios, groups, and positions for current allocation
  const fetchData = useCallback(async () => {
    try {
      const [portfolioRes, groupRes, positionsRes] = await Promise.all([
        axios.get('/api/dimensions/portfolios'),
        axios.get('/api/portfolio-groups'),
        axios.get('/api/positions/latest')
      ]);
      const portfolios = portfolioRes.data?.content || portfolioRes.data || [];
      const groups = groupRes.data?.content || groupRes.data || [];
      const positions = positionsRes.data?.content || positionsRes.data || [];

      // Attach positions to their portfolios for the AlternativesView
      const positionsByPortfolio: Record<number, any[]> = {};
      (Array.isArray(positions) ? positions : []).forEach((pos: any) => {
        const pid = pos.portfolioId;
        if (pid) {
          if (!positionsByPortfolio[pid]) positionsByPortfolio[pid] = [];
          positionsByPortfolio[pid].push(pos);
        }
      });
      const enriched = (Array.isArray(portfolios) ? portfolios : []).map((p: any) => ({
        ...p,
        positions: positionsByPortfolio[p.id] || [],
      }));

      setPortfolios(enriched);
      setPortfolioGroups(Array.isArray(groups) ? groups : []);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /** A fund's market value: the record's own figure, else the sum of its latest positions. */
  const portfolioMarketValue = (p: any): number => {
    const own = parseFloat(p.totalMarketValue);
    if (own > 0) return own;
    return (p.positions || []).reduce((sum: number, pos: any) => sum + Math.max(0, parseFloat(pos.marketValue || pos.currentValue || 0) || 0), 0);
  };

  const getActivePortfolios = () => {
    if (selectionMode === 'clo') {
      return lane.managed.filter(p => lane.selected.includes(p.portfolioId)).map(p => ({ id: p.portfolioId, portfolioName: p.portfolioName }));
    }
    if (selectionMode === 'individual' && selectedPortfolioIds.length > 0) {
      return portfolios.filter(p => selectedPortfolioIds.includes(p.id));
    }
    if (selectionMode === 'group' && selectedGroupId) {
      const group = portfolioGroups.find(g => g.id === selectedGroupId);
      return group?.portfolios || [];
    }
    return portfolios;
  };

  // Typeahead: two characters in, suggestions from the security master; a pick fills the card.
  useEffect(() => {
    const q = searchCusip.trim();
    if (!showSecurityModal || q.length < 2) { setSecuritySuggestions([]); return; }
    let cancelled = false;
    const handle = setTimeout(() => {
      axios.get('/api/dimensions/securities/search', { params: { query: q, limit: 8 } })
        .then(res => { if (!cancelled) setSecuritySuggestions(Array.isArray(res.data?.content) ? res.data.content : Array.isArray(res.data) ? res.data : []); })
        .catch(() => { if (!cancelled) setSecuritySuggestions([]); });
    }, 200);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [searchCusip, showSecurityModal]);

  const pickSecurity = async (sec: any) => {
    try {
      const full = sec?.id ? (await axios.get(`/api/dimensions/securities/${sec.id}`)).data : sec;
      const chosen = full || sec;
      if (chosen && !selectedSecurities.find(s => s.cusip === chosen.cusip)) setSelectedSecurities([...selectedSecurities, chosen]);
      setSearchCusip(''); setSecuritySuggestions([]); setShowSecurityModal(false);
    } catch (err) {
      setError('Could not load that security');
    }
  };

  const searchSecurity = async () => {
    if (!searchCusip.trim()) return;
    if (securitySuggestions.length === 1) { await pickSecurity(securitySuggestions[0]); return; }
    try {
      const response = await axios.get(`/api/dimensions/securities/cusip/${searchCusip}`);
      if (response.data && !selectedSecurities.find(s => s.cusip === response.data.cusip)) {
        setSelectedSecurities([...selectedSecurities, response.data]);
      }
      setSearchCusip('');
      setShowSecurityModal(false);
    } catch (err) {
      setError(`Security ${searchCusip} not found`);
    }
  };

  /**
   * One box. The parser classifies the text: an instruction becomes a Parsed Intent card (nothing is
   * applied until Apply Intent); a question, or anything the parser cannot read as a trade, goes to
   * the assistant and comes back as an answer in the conversation.
   */
  const submitCommand = async () => {
    const text = nlInput.trim();
    if (!text) return;
    setNlLoading(true);
    setError(null);
    setBlockingOperation({ isActive: true, message: 'Reading your command…', subMessage: 'An instruction fills the form; a question gets an answer' });
    try {
      const response = await axios.post('/api/ai/multi-portfolio/parse-intent', { input: text });
      const intent = response.data;
      if (intent.parsed) {
        // Anything the parser could not tie to the book (an identifier not on file) is said before Apply Intent.
        const warnings: string[] = Array.isArray(intent.warnings) ? intent.warnings : [];
        const reply = warnings.length
          ? `Here is what I read, with ${warnings.length === 1 ? 'one thing' : 'some things'} to fix first: ${warnings.join(' ')}`
          : 'Here is what I read. Check it, then Apply Intent to fill the form.';
        setChatMessages(prev => [...prev, { role: 'user', content: text }, { role: 'assistant', content: reply }]);
        setAppliedIntentText(text);
        setNlInput('');
        setAiSuggestion({ type: 'intent', data: { ...intent, originalInput: text } });
      } else if (intent.kind === 'CLARIFICATION' && intent.message) {
        // The command named something that is not on file (a portfolio group): ask, keep the text to edit.
        setChatMessages(prev => [...prev, { role: 'user', content: text }, { role: 'assistant', content: intent.message }]);
      } else {
        setNlInput('');
        await sendChatMessage(text);
      }
    } catch (err: any) {
      const status = err.response?.status;
      if (status === 400) {
        // Not a trade the parser could read: treat it as a question rather than a dead end.
        setNlInput('');
        await sendChatMessage(text);
      } else if (status === 403) {
        setError('Access denied. Please ensure you are logged in with sufficient permissions.');
      } else if (status === 503) {
        setError(err.response?.data?.error || 'The assistant is not available for this tenant.');
      } else if (status === 401) {
        setError('Session expired. Please log in again.');
      } else {
        setError(err.response?.data?.error || err.message || 'The command could not be read');
      }
    } finally {
      setNlLoading(false);
      setBlockingOperation({ isActive: false, message: '' });
    }
  };

  const formatAIResponse = (message: string): string => {
    if (!message) return message;
    let formatted = message;
    formatted = formatted.replace(/```json\s*/g, '').replace(/```\s*/g, '');
    return formatted;
  };

  const sendChatMessage = async (text: string) => {
    if (!text.trim()) return;
    const userMessage: ChatMessage = { role: 'user', content: text.trim() };
    setChatMessages(prev => [...prev, userMessage]);
    setChatLoading(true);
    setBlockingOperation({
      isActive: true,
      message: 'AI Thinking...',
      subMessage: 'Analyzing your question and generating response'
    });

    try {
      const activePortfolios = getActivePortfolios();
      const context = {
        portfolios: activePortfolios.map((p: any) => ({
          id: p.id,
          name: p.portfolioName,
          marketValue: p.totalMarketValue || 0,
          duration: p.duration || 0
        })),
        securities: selectedSecurities.map(s => ({
          cusip: s.cusip,
          description: s.description,
          duration: s.duration
        })),
        ...(lane.chatContext ? { cloAnalysis: lane.chatContext } : {})
      };

      const response = await axios.post('/api/ai/multi-portfolio/chat', {
        messages: [...chatMessages, userMessage].map(m => ({
          role: m.role,
          content: m.content
        })),
        context
      });

      const aiResponse = response.data;
      const rawMessage = aiResponse.message || aiResponse.error || 'No response';
      const formattedMessage = formatAIResponse(rawMessage);
      
      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: formattedMessage
      };
      setChatMessages(prev => [...prev, assistantMessage]);
      if (aiResponse.suggestedAllocation?.allocations) {
        setAiSuggestion({ type: 'chat_allocation', data: aiResponse.suggestedAllocation });
      }
    } catch (err: any) {
      const errorMessage: ChatMessage = { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' };
      setChatMessages(prev => [...prev, errorMessage]);
    } finally {
      setChatLoading(false);
      setBlockingOperation({ isActive: false, message: '' });
    }
  };

  const getAISuggestion = async (overrideAmount?: number | null) => {
    const activePortfolios = getActivePortfolios();
    if (selectedSecurities.length === 0 || activePortfolios.length === 0) {
      setError('Please select security and portfolios first');
      return;
    }
    setAiSuggestLoading(true);
    setBlockingOperation({ isActive: true, message: 'Generating AI Allocation...' });
    try {
      const targetValue = overrideAmount !== undefined ? overrideAmount : (totalAmountFromNLP || Object.values(targets).reduce((sum, t) => sum + parseFloat(t || '0'), 0) || null);
      const response = await axios.post('/api/ai/multi-portfolio/suggest-allocation', {
        securityId: selectedSecurities[0].id,
        portfolioIds: activePortfolios.map((p: any) => p.id),
        direction: direction.toUpperCase(),
        targetMode,
        targetValue,
        asOfDate
      });
      if (response.data.allocations) {
        const newTargets: {[key: string]: string} = {};
        response.data.allocations.forEach((alloc: any) => { newTargets[alloc.portfolioId] = alloc.amount.toString(); });
        setTargets(newTargets);
      }
      setAiSuggestion({ type: 'allocation', data: response.data });
    } catch (err: any) { setError('Failed to get AI suggestion'); } finally {
      setAiSuggestLoading(false);
      setBlockingOperation({ isActive: false, message: '' });
    }
  };

  const applyParsedIntent = async () => {
    if (!aiSuggestion || aiSuggestion.type !== 'intent') return;
    const intent = aiSuggestion.data;
    setAppliedIntentText(intent?.originalInput || intent?.rawInput || nlInput?.trim() || 'AI assistant intent');

    // Set trade direction
    if (intent.action) setDirection(intent.action.toLowerCase() as any);
    // A loan purchase: price, par, weights, seller and method go to the managed-CLO lane.
    setPendingLoanIntent({ price: intent.price, accruedPerPar: intent.accruedPerPar, assignmentFee: intent.assignmentFee, seller: intent.seller,
      amount: intent.amount, amountType: intent.amountType, allocationMethod: intent.allocationMethod, targetWeights: intent.targetWeights,
      portfolioPars: intent.portfolioPars, portfolioIds: intent.portfolioIds, portfolioGroupId: intent.portfolioGroupId });

    // Set amount from NLP
    if (intent.amount && intent.amountType === 'DOLLARS') {
      setTotalAmountFromNLP(intent.amount);
    }

    // Handle portfolio selection from intent
    let portfoliosSelected = false;
    let selectedIds: number[] = [];
    if (intent.portfolioIds && intent.portfolioIds.length > 0) {
      // Use specific portfolio IDs from intent (the managed-CLO lane keeps its own selection).
      // A named group is shown as that group, not as a hand-picked list (F-CB-029).
      const namedGroup = intent.portfolioGroupId ? portfolioGroups.find(g => g.id === intent.portfolioGroupId) : null;
      if (!loanSecurity) {
        if (namedGroup) { setSelectionMode('group'); setSelectedGroupId(namedGroup.id); }
        else setSelectionMode('individual');
      }
      setSelectedPortfolioIds(intent.portfolioIds);
      selectedIds = intent.portfolioIds;
      portfoliosSelected = true;
    } else if (intent.portfolioNames && intent.portfolioNames.length > 0) {
      // Match by portfolio names
      const matchedIds = portfolios
        .filter(p => intent.portfolioNames.some((name: string) => {
          const pName = (p.portfolioName || p.name || '').toLowerCase();
          const targetName = name.toLowerCase();
          return pName.includes(targetName) || targetName.includes(pName);
        }))
        .map(p => p.id);
      if (matchedIds.length > 0) {
        setSelectionMode('individual');
        setSelectedPortfolioIds(matchedIds);
        selectedIds = matchedIds;
        portfoliosSelected = true;
      }
    }

    // No silent select-ALL fallback (F-TB-025): an unmatched portfolio clause
    // must fail loudly rather than quietly staging a whole-book trade setup.
    if (!portfoliosSelected) {
      setAiSuggestion(null);
      setError('Could not match any portfolios for: '
        + (intent.portfolioNames?.length ? intent.portfolioNames.join(', ') : 'no portfolio criteria given')
        + '. Select portfolios manually or rephrase the command.');
      return;
    }

    // "The on-the-run 10-year" not yet in the security master: Apply adds it from Treasury's auction record.
    if (intent.onTheRunNeedsSecurity && intent.onTheRun?.tenor) {
      try {
        const res = await axios.post(`/api/reference/treasury/on-the-run/${encodeURIComponent(intent.onTheRun.tenor)}/security`);
        intent.securityId = res.data?.securityId;
      } catch (e: any) {
        setAiSuggestion(null);
        setError(`Could not add ${intent.onTheRun.name || intent.onTheRun.cusip} to the security master: ${e.response?.data?.message || e.response?.data?.error || e.message}`);
        return;
      }
    }

    // Handle security lookup
    let foundSecurity: any = null;

    // 1. Prefer security already resolved by backend
    if (intent.securityId) {
      try {
        const res = await axios.get(`/api/dimensions/securities/${intent.securityId}`);
        if (res.data) {
          setSelectedSecurities([res.data]);
          foundSecurity = res.data;
        }
      } catch (e) {
        console.log('Security ID lookup failed:', intent.securityId);
      }
    }

    // 2. Try CUSIP lookup if ID failed or wasn't provided
    if (!foundSecurity && intent.cusip) {
      try {
        const res = await axios.get(`/api/dimensions/securities/cusip/${intent.cusip}`);
        if (res.data) {
          setSelectedSecurities([res.data]);
          foundSecurity = res.data;
        }
      } catch (e) {
        console.log('CUSIP lookup failed:', intent.cusip);
      }
    }

    // 3. If still not found, try searching by description
    if (!foundSecurity && intent.securityDescription) {
      try {
        const res = await axios.get(`/api/dimensions/securities/search`, {
          params: { query: intent.securityDescription }
        });
        if (res.data && res.data.length > 0) {
          setSelectedSecurities([res.data[0]]);
          foundSecurity = res.data[0];
        }
      } catch (e) {
        console.log('Security search failed:', intent.securityDescription);
      }
    }

    // Clear the suggestion card
    setAiSuggestion(null);

    if (foundSecurity && intent.allocationMethod === 'CLOSE_DURATION_GAP') {
      await runDurationGap(foundSecurity, intent, selectedIds);
      return;
    }

    // Size the funds the way the command said (F-CB-030). The AI allocation stays behind its own button.
    if (foundSecurity && selectedIds.length > 0) {
      if (isLeveragedLoan(foundSecurity)) return; // the managed-CLO lane sizes from pendingLoanIntent
      const members = selectedIds.map(id => portfolios.find(p => p.id === id)).filter(Boolean) as any[];
      const amount = intent.amountType === 'DOLLARS' && intent.amount > 0 ? Number(intent.amount) : null;
      const byName = (table: Record<string, any> | null | undefined) => {
        if (!table) return null;
        const out: Record<number, number> = {};
        for (const [name, v] of Object.entries(table)) {
          const m = members.find(p => String(p.portfolioName || p.name || '').toLowerCase().includes(name.toLowerCase()) || name.toLowerCase().includes(String(p.portfolioName || p.name || '').toLowerCase()));
          if (m) out[m.id] = Number(v) || 0;
        }
        return Object.keys(out).length ? out : null;
      };
      const pars = byName(intent.portfolioPars);
      const weights = byName(intent.targetWeights);
      let split: Record<number, number> | null = null;
      let how = '';
      if (pars) { split = pars; how = 'the amounts you gave'; }
      else if (amount != null) {
        let basis: Record<number, number>;
        if (weights) { basis = weights; how = 'the weights you gave'; }
        else if (intent.allocationMethod === 'EQUAL') { basis = Object.fromEntries(members.map(p => [p.id, 1])); how = 'equal amounts'; }
        else {
          basis = Object.fromEntries(members.map(p => [p.id, portfolioMarketValue(p)]));
          const anyMv = Object.values(basis).some(v => v > 0);
          if (!anyMv) basis = Object.fromEntries(members.map(p => [p.id, 1]));
          how = anyMv ? 'pro rata by market value' : 'equal amounts (no market values on file)';
        }
        const total = Object.values(basis).reduce((a, b) => a + b, 0);
        split = {};
        let assigned = 0;
        members.forEach((p, i) => {
          const share = i === members.length - 1 ? amount - assigned : Math.round(amount * (basis[p.id] || 0) / total);
          split![p.id] = share; assigned += share;
        });
      }
      if (split) {
        setTargetMode('amount');
        setTargets(Object.fromEntries(Object.entries(split).map(([id, v]) => [id, String(v)])));
        const lines = members.map(p => `${p.portfolioName || p.name} $${Number(split![p.id] || 0).toLocaleString('en-US')}`).join(' · ');
        setChatMessages(prev => [...prev, { role: 'assistant', content: `Sized ${how}: ${lines}. Change any amount in Set Portfolio Targets, or ask for an AI suggestion from the button below.` }]);
      } else {
        setChatMessages(prev => [...prev, { role: 'assistant', content: 'No amount in the command. Set the targets, or type the size.' }]);
      }
    } else {
      console.warn('Apply Intent skipped: security or portfolios missing', { foundSecurity, selectedIds });
      if (!foundSecurity) setError('Could not resolve security: ' + (intent.securityDescription || intent.cusip || 'Unknown'));
      else if (selectedIds.length === 0) setError('No matching portfolios found for: ' + (intent.portfolioNames?.join(', ') || 'Unknown'));
    }
  };

  // Portfolio Group Management Functions
  const handleCreateGroup = async () => {
    if (!groupForm.groupName.trim()) {
      setError('Group name is required');
      return;
    }
    try {
      await axios.post('/api/portfolio-groups', {
        groupName: groupForm.groupName,
        description: groupForm.description || ''
      });
      await fetchData();
      setGroupForm({ groupName: '', description: '' });
      setEditingGroup(null);
      setShowGroupManager(false);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create group');
    }
  };

  const handleUpdateGroup = async () => {
    if (!editingGroup || !groupForm.groupName.trim()) {
      setError('Group name is required');
      return;
    }
    try {
      await axios.put(`/api/portfolio-groups/${editingGroup.id}`, {
        groupName: groupForm.groupName,
        description: groupForm.description || ''
      });
      await fetchData();
      setGroupForm({ groupName: '', description: '' });
      setEditingGroup(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update group');
    }
  };

  const handleDeleteGroup = async (groupId: number) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await axios.delete(`/api/portfolio-groups/${groupId}`);
      await fetchData();
      if (selectedGroupId === groupId) setSelectedGroupId(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete group');
    }
  };

  const handleAddPortfoliosToGroup = async (groupId: number, portfolioIds: number[]) => {
    try {
      await axios.post(`/api/portfolio-groups/${groupId}/portfolios`, { portfolioIds });
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add portfolios');
    }
  };

  const handleRemovePortfolioFromGroup = async (groupId: number, portfolioId: number) => {
    try {
      await axios.delete(`/api/portfolio-groups/${groupId}/portfolios/${portfolioId}`);
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to remove portfolio');
    }
  };

  /**
   * Close duration gap: the engine reads each candidate's duration from the book, decides who is short of
   * target and splits the face amount pro rata to the DV01 shortfalls. The funds it sized become this
   * trade's targets at the engine's price, so Execute and Create Trade book exactly what it proposed.
   */
  const runDurationGap = async (security: any, intent: any, candidateIds: number[]) => {
    setBlockingOperation({ isActive: true, message: 'Measuring each fund\u2019s duration against its target…', subMessage: 'From the official book on the business date' });
    try {
      const res = await axios.post('/api/portfolio-construction/duration-gap/analyze', {
        securityId: security.id, cusip: security.cusip, requestedPar: Number(intent.amount),
        portfolioIds: intent.portfolioGroupId ? null : candidateIds, portfolioGroupId: intent.portfolioGroupId || null,
        cleanPrice: intent.price || null,
      });
      const gap = res.data;
      setDurationGap(gap);
      const funded = (gap.funds || []).filter((f: any) => Number(f.proposedPar) > 0);
      if (funded.length > 0) {
        setSelectionMode('individual');
        setSelectedPortfolioIds(funded.map((f: any) => f.portfolioId));
        setTargetMode('amount');
        setTargets(Object.fromEntries(funded.map((f: any) => [String(f.portfolioId), String(f.proposedPar)])));
      }
      const notSized = (gap.funds || []).filter((f: any) => !(Number(f.proposedPar) > 0));
      const sized = funded.map((f: any) => `${f.portfolioName} ${parShort(f.proposedPar)}`).join(' · ');
      const why = notSized.map((f: any) => `${f.portfolioName}: ${stateWords(f)}`).join('; ');
      setChatMessages(prev => [...prev, { role: 'assistant', content:
        (funded.length ? `Sized pro rata to each fund's DV01 shortfall: ${sized}.` : 'No fund could be sized.')
        + (Number(gap.residualPar) > 0 ? ` ${parShort(gap.residualPar)} left over: the short funds' gaps and cash take no more.` : '')
        + (why ? ` Not sized: ${why}.` : '') }]);
    } catch (e: any) {
      setDurationGap(null);
      setError(e.response?.data?.message || e.response?.data?.error || 'The duration gap analysis failed.');
    } finally {
      setBlockingOperation({ isActive: false, message: '' });
    }
  };

  /** Books the engine's proposal as DRAFT tickets, one per fund, at the engine's price: no second sizing. */
  const createDurationGapTrade = async () => {
    const g = durationGap;
    const funded = (g?.funds || []).filter((f: any) => Number(f.proposedPar) > 0);
    if (!g || funded.length === 0) { setError('Nothing to book: no fund was sized.'); return; }
    setLoading(true);
    setBlockingOperation({ isActive: true, message: 'Creating tickets…', subMessage: 'One DRAFT ticket per fund, at the sizing price' });
    try {
      const why = appliedIntentText ? `"${appliedIntentText}"` : 'Close duration gap';
      const legs = funded.map((f: any) => ({
        transactionType: 'BUY',
        securityId: g.securityId,
        portfolioId: f.portfolioId,
        amount: Number(f.proposedPar),
        price: Number(g.price.clean),
        tradeDate: g.tradeDate,
        status: 'DRAFT',
        rationale: `Close duration gap: ${why}`,
        notes: `Close duration gap ${g.engine}: duration ${f.durationBefore} to ${f.durationAfter} against target ${f.targetDuration}; `
          + `DV01 shortfall ${f.dv01Shortfall}; price ${g.price.clean} (${g.price.source}).`,
      }));
      const response = await axios.post('/api/trade-transactions/multi-leg', {
        type: 'SWAP',
        name: `Close duration gap BUY - ${g.securityName}`,
        legs,
      });
      setCreatedOrders(response.data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to create the tickets');
    } finally {
      setLoading(false);
      setBlockingOperation({ isActive: false, message: '' });
    }
  };

  const stateWords = (f: any) => ({
    NOT_SHORT: `at or above target (${f.durationBefore} vs ${f.targetDuration})`,
    NO_TARGET: 'no target duration',
    INCOMPLETE: (f.issues || [])[0] || 'holdings missing analytics',
    NO_MARKET_VALUE: 'no positive market value',
    SHORT: f.binding === 'CASH' ? 'no cash to buy with' : 'gap smaller than one increment',
  } as Record<string, string>)[f.state] || f.state;

  const executeTrade = async () => {
    const activePortfolios = getActivePortfolios();
    const allocations = activePortfolios.map((p: any) => ({
      portfolioId: p.id,
      amount: parseAmountShorthand(targets[`${p.id}`] || '0')
    })).filter((a: any) => a.amount > 0);

    if (allocations.length === 0) {
      setError('Please enter target values.');
      return;
    }

    setLoading(true);
    setBlockingOperation({ isActive: true, message: 'Executing Trade...' });
    try {
      const response = await axios.post('/api/portfolio-construction/multi-portfolio/allocate', {
        portfolioIds: activePortfolios.map((p: any) => p.id),
        cusip: selectedSecurities[0].cusip,
        transactionType: direction.toUpperCase(),
        allocationMethod: 'ABSOLUTE',
        allocations,
        settleDate: asOfDate,
        ...(durationGap?.cusip && durationGap.cusip === selectedSecurities[0].cusip && durationGap.price?.clean
          ? { price: durationGap.price.clean } : {})
      });
      // Always materialize portfolios as an array — downstream render reads
      // results.portfolios.length / .map unconditionally, so a missing
      // after.portfolios in the response must not bleed undefined into state.
      const afterPortfolios: any[] = Array.isArray(response.data?.after?.portfolios)
        ? response.data.after.portfolios
        : [];
      const beforePortfolios: any[] = Array.isArray(response.data?.before?.portfolios)
        ? response.data.before.portfolios
        : [];
      const respAllocations: any[] = Array.isArray(response.data?.allocations)
        ? response.data.allocations
        : [];
      const respLiquidity: any[] = Array.isArray(response.data?.liquidityImpact) ? response.data.liquidityImpact : [];
      setResults({
        portfolios: afterPortfolios.map((ap: any) => {
          const bp = beforePortfolios.find((b: any) => b.portfolioId === ap.portfolioId) || {};
          const alloc = respAllocations.find((a: any) => a.portfolioId === ap.portfolioId) || {};
          const liq = respLiquidity.find((l: any) => l.portfolioId === ap.portfolioId);
          return {
            id: ap.portfolioId,
            name: ap.portfolioName,
            beforeDur: parseFloat(bp.weightedDuration) || 0,
            afterDur: parseFloat(ap.weightedDuration) || 0,
            afterVal: parseFloat(ap.totalMarketValue) || 0,
            allocatedAmount: alloc.amount || 0,
            liquidityPercentAfter: liq?.liquidityPercentAfter != null ? Number(liq.liquidityPercentAfter) : null,
            complianceReady: alloc.complianceReady ?? null,
            complianceErrors: Array.isArray(alloc.complianceErrors) ? alloc.complianceErrors : []
          };
        }),
        totals: {
          durChange: parseFloat(response.data?.changes?.durationChange) || 0,
          valChange: parseFloat(response.data?.changes?.valueChange) || 0
        },
        // F-TB-020: the resolved price the analysis used; Create Order books at this price.
        price: response.data?.price != null && Number.isFinite(Number(response.data.price)) ? Number(response.data.price) : undefined,
        priceSource: response.data?.priceSource ?? undefined
      });
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Execution failed');
    } finally {
      setLoading(false);
      setBlockingOperation({ isActive: false, message: '' });
    }
  };

  const handleCreateOrder = async () => {
    if (!results || !results.portfolios || results.portfolios.length === 0) {
      setError('No analysis results to create orders from.');
      return;
    }

    if (selectedSecurities.length === 0) {
      setError('No security selected.');
      return;
    }

    setLoading(true);
    setBlockingOperation({ isActive: true, message: 'Creating Orders...' });

    try {
      const security = selectedSecurities[0];
      const tradeDate = getTenantToday();
      
      const legs = results.portfolios.filter((p: any) => p.allocatedAmount > 0).map((p: any) => ({
        transactionType: direction.toUpperCase(),
        securityId: security.id,
        portfolioId: p.id,
        amount: p.allocatedAmount,
        // F-TB-020: the ticket must book at the SAME price the approved
        // analysis showed — the allocate response's resolved price is
        // authoritative; security-master fields are a fallback only.
        price: results?.price ?? (security.currentPrice || security.price || security.lastTradePrice),
        tradeDate: tradeDate,
        // settleDate deliberately omitted: the backend derives it from trade
        // date + the security's class convention (F-TB-018 — the analysis
        // as-of is a valuation context, not a settlement instruction).
        status: 'DRAFT',
        // F-TB-022: provenance must be true. Only claim AI assistance when an
        // NL intent actually shaped this setup; notes persist the trail.
        rationale: appliedIntentText
          ? `AI-assisted allocation: "${appliedIntentText}"`
          : 'Multi-portfolio construction',
        notes: appliedIntentText
          ? `AI-assisted allocation: "${appliedIntentText}"`
          : 'Multi-portfolio construction'
      }));

      if (legs.length === 0) {
        setError('No non-zero allocations found.');
        setLoading(false);
        setBlockingOperation({ isActive: false, message: '' });
        return;
      }

      const response = await axios.post('/api/trade-transactions/multi-leg', {
        type: 'SWAP', // Default type for multi-portfolio allocation
        name: `Multi-Portfolio ${direction.toUpperCase()} - ${security.description || security.cusip}`,
        legs: legs
      });

      setCreatedOrders(response.data || []);
    } catch (err: any) {
      console.error('Order creation error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to create orders');
    } finally {
      setLoading(false);
      setBlockingOperation({ isActive: false, message: '' });
    }
  };

  const handleNewTrade = () => {
    lane.reset();
    setResults(null);
    setDurationGap(null);
    setCreatedOrders(null);
    setSelectedSecurities([]);
    setTargets({});
    setAiSuggestion(null);
    setError(null);
  };

  // Examples the box accepts, built from this tenant's data where it exists.
  const commandExamples = useMemo(() => {
    const group = portfolioGroups[0]?.groupName;
    const where = group ? `across the ${group} group` : 'across all portfolios';
    if (loanSecurity || cloDeepLink.managedClo) {
      return [
        `Buy 7MM of ${loanSecurity?.cusip || 'LL9000000'} at 98.50 from Cedar Street Capital, accrued 0.004375 per par, assignment fee 3,500 each, ${where}, size to capacity`,
        'How much room does each CLO have for this loan, and what stops it?',
      ];
    }
    return [`Buy $10M of ${selectedSecurities[0]?.cusip || 'a CUSIP'} ${where}`, 'Which of these portfolios has the most room for this trade?'];
  }, [portfolioGroups, loanSecurity, cloDeepLink.managedClo, selectedSecurities]);

  // What each section still needs; the ticks and the next-steps panel both read this.
  const done = {
    security: selectedSecurities.length > 0,
    portfolios: loanSecurity ? lane.selected.length > 0 : getActivePortfolios().length > 0,
    loanTerms: !!loanSecurity && parseAmountShorthand(lane.terms.cleanPrice) > 0 && (lane.terms.transferForm !== 'ASSIGNMENT' || !!lane.terms.sellerCounterparty.trim()),
    target: loanSecurity ? lane.ready : (Object.values(targets).some(v => parseAmountShorthand(String(v)) > 0) || totalAmountFromNLP != null),
  };

  /** The Close duration gap sizing: who is short of target, by how much, and what each fund gets. */
  const renderDurationGap = () => {
    const g = durationGap;
    const pr = g.price || {};
    const num = (v: any, dp = 2) => (v == null || v === '' ? '—' : Number(v).toFixed(dp));
    const cell: React.CSSProperties = { padding: '8px 10px', borderBottom: '1px solid rgba(148,163,184,0.12)', textAlign: 'right', whiteSpace: 'nowrap' };
    const head: React.CSSProperties = { ...cell, color: '#94a3b8', fontWeight: 600, fontSize: 12 };
    const stateColor: Record<string, string> = { SHORT: '#34d399', NOT_SHORT: '#94a3b8', NO_TARGET: '#94a3b8', INCOMPLETE: '#fbbf24', NO_MARKET_VALUE: '#fbbf24' };
    const stateLabel: Record<string, string> = { SHORT: 'Short', NOT_SHORT: 'Not short', NO_TARGET: 'No target', INCOMPLETE: 'Not measurable', NO_MARKET_VALUE: 'No market value' };
    return (
      <ResultCard style={{ marginBottom: 16 }}>
        <ResultHeader>
          <div>
            <ResultTitle>Close duration gap</ResultTitle>
            <ResultSubtitle>
              {g.securityName} ({g.cusip}) · {num(pr.clean, 4)} clean + {num(pr.accruedPer100, 4)} accrued · yield {num(pr.yieldPct, 3)}% · mod. duration {num(pr.modifiedDuration, 2)} · DV01 {money(pr.dv01Per1MM)} per 1MM · trade {g.tradeDate} · settle {g.settleDate}
            </ResultSubtitle>
            <ResultSubtitle>Price: {pr.source}</ResultSubtitle>
          </div>
        </ResultHeader>
        <ActionButtonsContainer>
          {!createdOrders && Number(g.allocatedPar) > 0 && <ActionButton variant="primary" onClick={createDurationGapTrade} disabled={loading}>✓ Create Trade</ActionButton>}
          <ActionButton variant="secondary" onClick={handleNewTrade} disabled={loading}>➕ New Trade</ActionButton>
        </ActionButtonsContainer>
        <ImpactGrid>
          <ImpactCell><ImpactValue>{parShort(g.requestedPar)}</ImpactValue><ImpactLabel>Requested face</ImpactLabel></ImpactCell>
          <ImpactCell><ImpactValue>{parShort(g.allocatedPar)}</ImpactValue><ImpactLabel>Allocated</ImpactLabel></ImpactCell>
          <ImpactCell><ImpactValue negative={Number(g.residualPar) > 0}>{parShort(g.residualPar)}</ImpactValue><ImpactLabel>Residual</ImpactLabel></ImpactCell>
          <ImpactCell><ImpactValue>{money(g.totalCost)}</ImpactValue><ImpactLabel>Cash required</ImpactLabel></ImpactCell>
        </ImpactGrid>
        <div style={{ overflowX: 'auto', padding: '0 24px 8px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', color: '#e2e8f0', fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ ...head, textAlign: 'left' }}>Fund</th>
                <th style={{ ...head, textAlign: 'left' }}>State</th>
                <th style={head}>Target</th>
                <th style={head}>Now</th>
                <th style={head}>After</th>
                <th style={head}>Gap (yrs)</th>
                <th style={head}>DV01 short</th>
                <th style={head}>Face</th>
                <th style={head}>Cost</th>
                <th style={head}>Cash after</th>
                <th style={{ ...head, textAlign: 'left' }}>Stops at</th>
              </tr>
            </thead>
            <tbody>
              {(g.funds || []).map((f: any) => (
                <React.Fragment key={f.portfolioId}>
                  <tr>
                    <td style={{ ...cell, textAlign: 'left' }}>{f.portfolioName}</td>
                    <td style={{ ...cell, textAlign: 'left', color: stateColor[f.state] || '#e2e8f0' }}>{stateLabel[f.state] || f.state}</td>
                    <td style={cell}>{num(f.targetDuration)}</td>
                    <td style={cell}>{num(f.durationBefore)}</td>
                    <td style={cell}>{Number(f.proposedPar) > 0 ? num(f.durationAfter) : '—'}</td>
                    <td style={cell}>{num(f.gapYears)}</td>
                    <td style={cell}>{f.dv01Shortfall == null ? '—' : money(f.dv01Shortfall)}</td>
                    <td style={{ ...cell, fontWeight: 600 }}>{Number(f.proposedPar) > 0 ? Number(f.proposedPar).toLocaleString('en-US') : '—'}</td>
                    <td style={cell}>{Number(f.proposedPar) > 0 ? money(f.cost) : '—'}</td>
                    <td style={cell}>{Number(f.proposedPar) > 0 ? money(f.cashAfter) : '—'}</td>
                    <td style={{ ...cell, textAlign: 'left' }}>{f.state === 'SHORT' ? (f.binding === 'CASH' ? 'Cash' : 'Target') : ''}</td>
                  </tr>
                  {(f.issues || []).length > 0 && (
                    <tr><td colSpan={11} style={{ ...cell, textAlign: 'left', color: '#94a3b8', fontSize: 12, paddingTop: 0 }}>{f.issues.join(' · ')}</td></tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          <div style={{ color: '#94a3b8', fontSize: 12, padding: '10px 0' }}>
            {g.methodLabel}. {g.note} Face amounts are in multiples of {Number(g.increment).toLocaleString('en-US')}. Create Trade books these amounts at this price as DRAFT tickets; pre-clear runs the compliance rules on each.
          </div>
        </div>
      </ResultCard>
    );
  };

  const renderNextSteps = () => {
    const missing = [
      !done.security && 'a security',
      !done.portfolios && 'the portfolios',
      loanSecurity ? (!done.loanTerms && 'the loan terms') : (!done.target && 'a size'),
    ].filter(Boolean) as string[];
    const allDone = missing.length === 0;
    const missingText = missing.length === 1 ? missing[0] : missing.slice(0, -1).join(', ') + ' and ' + missing[missing.length - 1];
    return (
      <EmptyResults style={{ textAlign: 'left', padding: '32px 36px' }}>
        <div className="flex items-start gap-4">
          <div className="shrink-0 w-11 h-11 rounded-2xl bg-indigo-500/15 border border-indigo-400/20 flex items-center justify-center"><Sparkles className="w-5 h-5 text-indigo-300" /></div>
          <div>
            <EmptyTitle style={{ fontSize: '1.25rem' }}>{allDone ? 'Ready to run' : 'Start with a command, or build it step by step'}</EmptyTitle>
            <EmptyText style={{ margin: 0, maxWidth: 640 }}>
              {loanSecurity
                ? 'Each managed CLO is sized against its settled cash and every indenture test. The result shows the par each can take, the test that stops it, and what the trade does to every number.'
                : 'A trade needs a security, the portfolios that buy it, and a size. The analysis shows every portfolio before and after, with the compliance tests pre-clear will run.'}
            </EmptyText>
          </div>
        </div>
        <div className={cn('mt-5 rounded-xl border px-4 py-3 text-sm', allDone ? 'border-emerald-400/25 bg-emerald-500/5 text-emerald-200' : 'border-white/5 bg-slate-950/30 text-slate-300')}>
          {allDone
            ? <>Everything is set. <span className="font-semibold text-white">Execute {direction.toUpperCase()} Analysis</span> runs it.</>
            : <>Still needed on the left: <span className="font-semibold text-white">{missingText}</span>. Or type the whole trade in the command bar above.</>}
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[['Impact', 'Every portfolio before and after the trade'], ['Compliance', 'Each test pre-clear will run, with its value'], ['Tickets', loanSecurity ? 'One block, one allocation per CLO' : 'One order per portfolio']].map(([t, d]) => (
            <div key={t} className="rounded-xl border border-white/5 bg-slate-950/30 p-3"><div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{t}</div><div className="text-xs text-slate-300 mt-1">{d}</div></div>
          ))}
        </div>
        {loanSecurity && lane.recent.length > 0 && (
          <div className="mt-6">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Saved analyses for this loan</div>
            {lane.recent.map(r => (
              <button key={r.id} onClick={() => lane.load(r.id)} className="w-full text-left rounded-lg border border-white/5 hover:border-indigo-500/40 bg-slate-900/40 p-2.5 mb-1.5 transition-colors">
                <div className="text-xs font-semibold text-slate-200">{r.label || r.id.slice(0, 8)} · {parShort(Number(r.allocatedPar))} of {parShort(Number(r.requestedPar))}</div>
                <div className="text-[10px] text-slate-500">{r.createdBy} · {String(r.createdAt).slice(0, 16)} · {r.tickets} ticket{r.tickets === 1 ? '' : 's'}</div>
              </button>
            ))}
          </div>
        )}
      </EmptyResults>
    );
  };

  return (
    <PageContainer>
      <PageHeader>
        <div className="flex justify-between items-start">
          <div>
            <PageTitle>Trade Builder</PageTitle>
            <PageSubtitle>Build and execute trades across multiple portfolios</PageSubtitle>
          </div>
        </div>
      </PageHeader>

      <CommandBar>
        <div className="flex items-center gap-3">
          <Sparkles className="w-5 h-5 text-indigo-300 shrink-0" />
          <CommandInput
            placeholder={loanSecurity || cloDeepLink.managedClo ? 'Tell it the loan, price, seller and the CLOs, or ask it a question' : 'Tell it what to buy or sell and for whom, or ask it a question'}
            value={nlInput}
            onChange={e => setNlInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submitCommand(); } }}
            disabled={nlLoading || chatLoading}
          />
          <AIButton variant="primary" onClick={submitCommand} disabled={nlLoading || chatLoading || !nlInput.trim()}>
            {nlLoading || chatLoading ? 'Working…' : <>Go <Send className="w-3.5 h-3.5" /></>}
          </AIButton>
        </div>
        <CommandHint>Say what to trade and it fills the form. Ask a question and it answers. Nothing is applied until you click Apply Intent.</CommandHint>
        {chatMessages.length === 0 && !aiSuggestion && (
          <div className="flex flex-wrap gap-2 mt-3">
            {commandExamples.map(ex => <ExampleChip key={ex} onClick={() => setNlInput(ex)}>{ex}</ExampleChip>)}
          </div>
        )}
        {chatMessages.length > 0 && (
          <div className="flex items-center justify-between mt-4 mb-1">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Conversation</span>
            <button className="text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:text-slate-300" onClick={() => { setChatMessages([]); setAiSuggestion(null); }}>Clear</button>
          </div>
        )}
        {chatMessages.length > 0 && (
          <AIChatMessages>
            {chatMessages.map((msg, idx) => (
              // SOX Compliance: sanitised before rendering (DOMPurify) so a model reply cannot inject markup.
              <AIChatMessage key={idx} role={msg.role} dangerouslySetInnerHTML={{ __html: sanitizeMarkdown(msg.content) }} />
            ))}
            {chatLoading && <AIChatMessage role="assistant"><span style={{ opacity: 0.6 }}>Thinking...</span></AIChatMessage>}
          </AIChatMessages>
        )}
            {aiSuggestion && (
              <AISuggestionCard>
                <AISuggestionHeader>
                  <AISuggestionTitle>✨ {aiSuggestion.type === 'intent' ? 'Parsed Intent' : 'AI Suggestion'}</AISuggestionTitle>
                  <div className="flex gap-2">
                    {aiSuggestion.type === 'intent' ? (
                      <AIButton variant="primary" onClick={applyParsedIntent}>Apply Intent</AIButton>
                    ) : (
                      <AIButton variant="primary" onClick={() => {
                        const newTargets: {[key: string]: string} = {};
                        aiSuggestion.data.allocations.forEach((alloc: any) => { newTargets[alloc.portfolioId] = alloc.amount.toString(); });
                        setTargets(newTargets);
                        setAiSuggestion(null);
                      }}>Apply Allocation</AIButton>
                    )}
                    <AIButton onClick={() => setAiSuggestion(null)}>Dismiss</AIButton>
                  </div>
                </AISuggestionHeader>
                {aiSuggestion.type === 'intent' && aiSuggestion.data && (
                  <div style={{ padding: '12px', fontSize: '13px', color: '#e0e0e0' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 16px' }}>
                      {aiSuggestion.data.action && (
                        <>
                          <span style={{ color: '#888' }}>Action:</span>
                          <span style={{ color: aiSuggestion.data.action === 'BUY' ? '#00c853' : '#ff4757', fontWeight: 600 }}>
                            {aiSuggestion.data.action}
                          </span>
                        </>
                      )}
                      {aiSuggestion.data.amount && (
                        <>
                          <span style={{ color: '#888' }}>Amount:</span>
                          <span>${(aiSuggestion.data.amount / 1000000).toFixed(1)}M ({aiSuggestion.data.amountType || 'DOLLARS'})</span>
                        </>
                      )}
                      {aiSuggestion.data.securityDescription && (
                        <>
                          <span style={{ color: '#888' }}>Security:</span>
                          <span>{aiSuggestion.data.securityDescription}</span>
                        </>
                      )}
                      {aiSuggestion.data.cusip && (
                        <>
                          <span style={{ color: '#888' }}>CUSIP:</span>
                          <span style={{ fontFamily: 'monospace' }}>{aiSuggestion.data.cusip}</span>
                        </>
                      )}
                      {aiSuggestion.data.portfolioNames && aiSuggestion.data.portfolioNames.length > 0 && (
                        <>
                          <span style={{ color: '#888' }}>Portfolios:</span>
                          <span>{aiSuggestion.data.portfolioNames.join(', ')}</span>
                        </>
                      )}
                      {aiSuggestion.data.allocationMethod && (
                        <>
                          <span style={{ color: '#888' }}>Allocation:</span>
                          <span>{aiSuggestion.data.allocationMethod.replace(/_/g, ' ')}</span>
                        </>
                      )}
                      {aiSuggestion.data.portfolioGroupName && (<><span style={{ color: '#888' }}>Group:</span><span>{aiSuggestion.data.portfolioGroupName}</span></>)}
                      {aiSuggestion.data.price != null && (<><span style={{ color: '#888' }}>Clean price:</span><span>{aiSuggestion.data.price}</span></>)}
                      {aiSuggestion.data.accruedPerPar != null && (<><span style={{ color: '#888' }}>Accrued per par:</span><span>{aiSuggestion.data.accruedPerPar}</span></>)}
                      {aiSuggestion.data.assignmentFee != null && (<><span style={{ color: '#888' }}>Assignment fee:</span><span>{Number(aiSuggestion.data.assignmentFee).toLocaleString('en-US')}</span></>)}
                      {aiSuggestion.data.seller && (<><span style={{ color: '#888' }}>Seller:</span><span>{aiSuggestion.data.seller}</span></>)}
                      {aiSuggestion.data.price != null && !aiSuggestion.data.targetWeights && !aiSuggestion.data.portfolioPars && (<><span style={{ color: '#888' }}>Split:</span><span>decided by the engine from each CLO's capacity (settled cash and indenture rules)</span></>)}
                      {aiSuggestion.data.targetWeights && (<><span style={{ color: '#888' }}>Weights:</span><span>{Object.entries(aiSuggestion.data.targetWeights).map(([k, v]) => `${k} ${v}`).join(' · ')}</span></>)}
                      {aiSuggestion.data.portfolioPars && (<><span style={{ color: '#888' }}>Par by CLO:</span><span>{Object.entries(aiSuggestion.data.portfolioPars).map(([k, v]) => `${k} ${Number(v).toLocaleString('en-US')}`).join(' · ')}</span></>)}
                      {aiSuggestion.data.confidence && (
                        <>
                          <span style={{ color: '#888' }}>Confidence:</span>
                          <span style={{ color: aiSuggestion.data.confidence > 0.8 ? '#00c853' : aiSuggestion.data.confidence > 0.5 ? '#ffc107' : '#ff4757' }}>
                            {(aiSuggestion.data.confidence * 100).toFixed(0)}%
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                )}
                {aiSuggestion.type === 'allocation' && aiSuggestion.data && (
                  <div style={{ padding: '12px', fontSize: '13px', color: '#e0e0e0' }}>
                    {aiSuggestion.data.recommendation && <div className="text-slate-300 mb-3">{aiSuggestion.data.recommendation}</div>}
                    <div className="space-y-2">
                      {(aiSuggestion.data.allocations || []).map((a: any) => (
                        <div key={a.portfolioId} className={cn('rounded-lg border px-3 py-2', Number(a.amount) > 0 ? 'border-white/10 bg-slate-950/30' : 'border-amber-400/30 bg-amber-500/5')}>
                          <div className="flex items-center justify-between gap-3">
                            <span className="font-semibold text-slate-100">{a.portfolioName || `Portfolio ${a.portfolioId}`}</span>
                            <span className={cn('font-mono', Number(a.amount) > 0 ? 'text-emerald-300' : 'text-amber-300')}>{Number(a.amount) > 0 ? `$${Number(a.amount).toLocaleString('en-US')}` : 'nothing'}</span>
                          </div>
                          {a.rationale && <div className="text-xs text-slate-400 mt-1">{a.rationale}</div>}
                        </div>
                      ))}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-3">An assistant's view, not a compliance result. Apply Allocation replaces the targets; the pre-clear tests on the Impact table decide what each fund may hold.</div>
                  </div>
                )}
              </AISuggestionCard>
            )}
      </CommandBar>

      {error && (
        <ErrorBanner>
          <span>{error}</span>
          <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', color: '#ff4757', cursor: 'pointer' }}>x</button>
        </ErrorBanner>
      )}

      {<ContentGrid>
        <BuilderPanel>
          <BuilderSection>
            <SectionTitle><SectionNumber>1</SectionNumber>Trade Direction<Tick on /></SectionTitle>
            <ChipGroup>
              <Chip selected={direction === 'buy'} color="#00c853" onClick={() => setDirection('buy')}>↗ Buy</Chip>
              <Chip selected={direction === 'sell'} color="#ff4757" disabled={!!loanSecurity} title={loanSecurity ? 'Loan disposals go through the leveraged-loan ticket' : undefined} onClick={() => !loanSecurity && setDirection('sell')}>↘ Sell</Chip>
            </ChipGroup>
          </BuilderSection>

          <BuilderSection>
            <SectionTitle><SectionNumber>2</SectionNumber>Security<Tick on={done.security} /></SectionTitle>
            {selectedSecurities.map(sec => (
              <SecurityCard key={sec.cusip}>
                <RemoveBtn onClick={() => setSelectedSecurities(selectedSecurities.filter(s => s.cusip !== sec.cusip))}>x</RemoveBtn>
                <SecurityCusip>{sec.cusip}</SecurityCusip>
                <SecurityName>{sec.securityName}</SecurityName>
                {isLeveragedLoan(sec) && (
                  <SecurityMeta>
                    <MetaItem><MetaLabel>Type</MetaLabel><MetaValue>{String(sec.securityType || 'LEVERAGED_LOAN').replace(/_/g, ' ')}</MetaValue></MetaItem>
                    <MetaItem><MetaLabel>Mark</MetaLabel><MetaValue>{sec.currentPrice ? Number(sec.currentPrice).toFixed(3) : 'none'}</MetaValue></MetaItem>
                    <MetaItem><MetaLabel>Maturity</MetaLabel><MetaValue>{sec.maturityDate || '—'}</MetaValue></MetaItem>
                  </SecurityMeta>
                )}
                {isPrivateAsset(sec) && (
                  <>
                    <LiquidityWarningBadge>
                      <AlertTriangle size={12} />
                      Illiquid Asset - Limited secondary market
                    </LiquidityWarningBadge>
                  </>
                )}
              </SecurityCard>
            ))}
            {selectedSecurities.length === 0 && (
              <AddSecurityBtn onClick={() => setShowSecurityModal(true)}>+ Add Security</AddSecurityBtn>
            )}
            {selectedSecurities.length === 0 && cloDeepLink.managedClo && (
              <div className="mt-2 text-[11px] text-indigo-200/80">Pick a leveraged loan to size it into the managed CLOs{cloDeepLink.portfolioId ? ` (portfolio ${cloDeepLink.portfolioId} preselected)` : ''}.</div>
            )}
          </BuilderSection>

          <BuilderSection>
            <SectionTitle><SectionNumber>3</SectionNumber>Portfolios<Tick on={done.portfolios} /></SectionTitle>
            <InputGroup>
              <ChipGroup>
                {(loanSecurity ? ['clo', 'group', 'individual'] : ['all', 'group', 'individual']).map(m => (
                  <Chip key={m} selected={selectionMode === m} onClick={() => setSelectionMode(m as any)}>{m === 'clo' ? 'Managed CLOs' : m.charAt(0).toUpperCase() + m.slice(1)}</Chip>
                ))}
              </ChipGroup>
            </InputGroup>

            {selectionMode === 'group' && (
              <div className="space-y-3 mt-4">
                <div className="flex items-center justify-between">
                  <InputLabel className="mb-0">Select Group</InputLabel>
                  <button 
                    onClick={() => setShowGroupManager(true)}
                    className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    <Plus className="w-3 h-3" /> Manage Groups
                  </button>
                </div>
                <StyledSelect 
                  value={selectedGroupId || ''} 
                  onChange={e => {
                    const id = e.target.value ? parseInt(e.target.value) : null;
                    setSelectedGroupId(id);
                    if (loanSecurity) { const g = portfolioGroups.find(x => x.id === id); if (g) lane.selectGroup(g); else lane.setSelected([]); }
                  }}
                >
                  <option value="">-- No Group Selected --</option>
                  {portfolioGroups.map(g => (
                    <option key={g.id} value={g.id}>{g.groupName} ({g.portfolios?.length || 0})</option>
                  ))}
                </StyledSelect>
                {loanSecurity && selectedGroupId && (
                  <div className="text-[11px] text-slate-400">
                    {lane.selected.length} managed CLO{lane.selected.length === 1 ? '' : 's'} selected from the group
                    {lane.excludedFromGroup.length > 0 && <span className="text-amber-300"> · not managed CLOs, excluded: {lane.excludedFromGroup.join(', ')}</span>}
                  </div>
                )}
              </div>
            )}

            {selectionMode === 'clo' && (
              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between">
                  <InputLabel className="mb-0">Portfolios with CLO indenture rules</InputLabel>
                  <div className="flex gap-3 text-[10px] font-bold uppercase tracking-wider">
                    <button className="text-blue-400 hover:text-blue-300" onClick={() => lane.setSelected(lane.managed.map(p => p.portfolioId))}>All</button>
                    <button className="text-slate-500 hover:text-slate-300" onClick={() => lane.setSelected([])}>None</button>
                  </div>
                </div>
                {lane.managed.length === 0 && <div className="text-xs text-slate-500">No portfolio has enabled CLO indenture rules. Import an indenture on the Compliance Rules screen first.</div>}
                {lane.selected.length > 1 && (
                  <div className="flex items-center gap-2">
                    <StyledInput value={groupNameDraft} onChange={e => setGroupNameDraft(e.target.value)} placeholder="Save selection as a group, e.g. CLO Portfolios" />
                    <ModalButton disabled={!groupNameDraft.trim()} onClick={async () => { try { await lane.saveAsGroup(groupNameDraft.trim()); setGroupNameDraft(''); fetchData(); } catch (e: any) { setError(e.response?.data?.error || 'Could not save the group'); } }}>Save</ModalButton>
                  </div>
                )}
                <div className="max-h-56 overflow-y-auto space-y-2 p-2 bg-slate-950/30 rounded-xl border border-white/5">
                  {lane.managed.map(p => {
                    const on = lane.selected.includes(p.portfolioId);
                    return (
                      <div key={p.portfolioId} onClick={() => lane.toggle(p.portfolioId)} className={cn("flex items-center justify-between p-2 rounded-lg cursor-pointer transition-all hover:bg-white/5", on ? "bg-blue-500/20 text-white" : "text-slate-500")}>
                        <div>
                          <div className="text-xs font-bold">{p.portfolioName}</div>
                          <div className="text-[10px] text-slate-500">{p.ruleCount} indenture tests · {p.dealName ? `${p.dealName} · ${p.noteClasses || 0} note classes` : 'no capital structure linked'}</div>
                          {(p.missingTests?.length || 0) > 0 && <div className="text-[10px] text-amber-300">Rulebook incomplete: no rule for {p.missingTests!.join(', ')}</div>}
                        </div>
                        {on && <CheckCircle2 className="w-4 h-4 text-blue-500" />}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {selectionMode === 'individual' && (
              <div className="max-h-48 overflow-y-auto space-y-2 mt-4 p-2 bg-slate-950/30 rounded-xl border border-white/5">
                {portfolios.map(p => (
                  <div key={p.id} onClick={() => setSelectedPortfolioIds(prev => prev.includes(p.id) ? prev.filter(id => id !== p.id) : [...prev, p.id])} className={cn("flex items-center justify-between p-2 rounded-lg cursor-pointer transition-all hover:bg-white/5", selectedPortfolioIds.includes(p.id) ? "bg-blue-500/20 text-white" : "text-slate-500")}>
                    <span className="text-xs font-bold">{p.portfolioName}</span>
                    {selectedPortfolioIds.includes(p.id) && <CheckCircle2 className="w-4 h-4 text-blue-500" />}
                  </div>
                ))}
              </div>
            )}
          </BuilderSection>

          {loanSecurity && (
            <BuilderSection>
              <SectionTitle><SectionNumber>4</SectionNumber>Loan Terms<Tick on={done.loanTerms} /></SectionTitle>
              <div className="grid grid-cols-2 gap-3">
                <InputGroup><InputLabel>Clean price *</InputLabel><StyledInput ref={cleanPriceRef} value={lane.terms.cleanPrice} onChange={e => lane.set('cleanPrice', e.target.value)} placeholder="98.50" /></InputGroup>
                <InputGroup><InputLabel>Accrued per 1 par</InputLabel><StyledInput value={lane.terms.accruedPerPar} onChange={e => lane.set('accruedPerPar', e.target.value)} placeholder="0.004375" /></InputGroup>
                <InputGroup><InputLabel>Assignment fee (per CLO)</InputLabel><StyledInput value={lane.terms.assignmentFee} onChange={e => lane.set('assignmentFee', e.target.value)} onBlur={amountBlur('assignmentFee', lane.set)} placeholder="3,500" /></InputGroup>
                <InputGroup><InputLabel>Fee payer</InputLabel>
                  <StyledSelect value={lane.terms.assignmentFeePayer} onChange={e => lane.set('assignmentFeePayer', e.target.value)}>
                    <option value="BUYER">Buyer</option><option value="SELLER">Seller</option><option value="WAIVED">Waived</option>
                  </StyledSelect></InputGroup>
                <InputGroup><InputLabel>Other fees</InputLabel><StyledInput value={lane.terms.otherFees} onChange={e => lane.set('otherFees', e.target.value)} onBlur={amountBlur('otherFees', lane.set)} /></InputGroup>
                <InputGroup><InputLabel>Transfer form</InputLabel>
                  <StyledSelect value={lane.terms.transferForm} onChange={e => lane.set('transferForm', e.target.value)}>
                    <option value="ASSIGNMENT">Assignment</option><option value="PARTICIPATION">Participation</option>
                  </StyledSelect></InputGroup>
                <InputGroup className="col-span-2"><InputLabel>Seller of record{lane.terms.transferForm === 'ASSIGNMENT' ? ' *' : ''}</InputLabel><StyledInput value={lane.terms.sellerCounterparty} onChange={e => lane.set('sellerCounterparty', e.target.value)} placeholder="Legal seller on the confirm" />
                  {lane.terms.transferForm === 'ASSIGNMENT' && !lane.terms.sellerCounterparty.trim() && <div className="mt-1 text-[11px] text-amber-300">Needed to create the trade; sizing runs without it.</div>}
                </InputGroup>
                <InputGroup><InputLabel>Trade date</InputLabel><DateField value={lane.terms.tradeDate} onChange={iso => lane.set('tradeDate', iso)} /></InputGroup>
                <InputGroup><InputLabel>Settle date</InputLabel><DateField value={lane.terms.settleDate} onChange={iso => lane.set('settleDate', iso)} /></InputGroup>
              </div>
              <div className="text-[11px] text-slate-500 mt-2">Economics per CLO: clean + accrued + fee. Settlement {lane.terms.settlementConvention ? `defaulted to LSTA ${lane.terms.settlementConvention.replace(/_/g, ' ').toLowerCase()} for this price` : 'defaults to the LSTA convention for the price'}. Amounts take M (thousands) and MM (millions).</div>
            </BuilderSection>
          )}

          <BuilderSection>
            <SectionTitle><SectionNumber>{loanSecurity ? 5 : 4}</SectionNumber>Target Configuration<Tick on={done.target} /></SectionTitle>
            {loanSecurity ? (
              <>
                <InputGroup>
                  <ChipGroup>
                    {([['SIZE_TO_CAPACITY', 'Size to capacity'], ['PRO_RATA', 'Pro rata'], ['ABSOLUTE', 'Absolute']] as [typeof lane.method, string][]).map(([m, text]) => (
                      <Chip key={m} selected={lane.method === m} onClick={() => lane.setMethod(m)}>{text}</Chip>
                    ))}
                  </ChipGroup>
                </InputGroup>
                <div className="text-[11px] text-slate-500 mb-3">
                  {lane.method === 'SIZE_TO_CAPACITY' && (lane.weightBasis === 'CAPACITY'
                    ? 'The engine decides the split: the request goes pro rata to each CLO\u2019s capacity, the largest par that passes its settled-cash test and every indenture rule, floored to the increment. The residual is shown.'
                    : 'Each CLO takes the largest par that passes every indenture test, split by the target weights you set, floored to the increment. The residual is shown.')}
                  {lane.method === 'PRO_RATA' && 'The requested par is split in proportion to each CLO’s collateral par, floored to the increment.'}
                  {lane.method === 'ABSOLUTE' && 'You set each CLO’s par in the targets; the matrix shows what passes.'}
                </div>
                {lane.method === 'SIZE_TO_CAPACITY' && (
                  <InputGroup>
                    <InputLabel>Split across CLOs</InputLabel>
                    <ChipGroup>
                      <Chip selected={lane.weightBasis === 'CAPACITY'} onClick={() => lane.setWeightBasis('CAPACITY')}>Auto · by capacity</Chip>
                      <Chip selected={lane.weightBasis === 'TARGET'} onClick={() => lane.setWeightBasis('TARGET')}>My weights</Chip>
                    </ChipGroup>
                  </InputGroup>
                )}
                <div className="grid grid-cols-2 gap-3">
                  {lane.method !== 'ABSOLUTE' && <InputGroup className="col-span-2"><InputLabel>Requested par</InputLabel><StyledInput value={lane.terms.requestedPar} onChange={e => lane.set('requestedPar', e.target.value)} onBlur={amountBlur('requestedPar', lane.set)} placeholder="7MM" /></InputGroup>}
                  <InputGroup><InputLabel>Increment</InputLabel><StyledInput value={lane.terms.increment} onChange={e => lane.set('increment', e.target.value)} onBlur={amountBlur('increment', lane.set)} /></InputGroup>
                  <InputGroup><InputLabel>Minimum ticket</InputLabel><StyledInput value={lane.terms.minimumTicket} onChange={e => lane.set('minimumTicket', e.target.value)} onBlur={amountBlur('minimumTicket', lane.set)} /></InputGroup>
                  <InputGroup className="col-span-2"><InputLabel>Analysis label (optional)</InputLabel><StyledInput value={lane.terms.label} onChange={e => lane.set('label', e.target.value)} placeholder="e.g. Nilesh $7MM across Cedar" /></InputGroup>
                </div>
              </>
            ) : (
              <>
                <InputGroup>
                  <ChipGroup>
                    {['percent', 'duration', 'spread', 'amount'].map(m => (
                      <Chip key={m} selected={targetMode === m} onClick={() => setTargetMode(m as any)}>{m === 'percent' ? '% of MV' : m.charAt(0).toUpperCase() + m.slice(1)}</Chip>
                    ))}
                  </ChipGroup>
                </InputGroup>
                <DateField value={asOfDate ?? ''} className="mt-4" onChange={iso => setAsOfDate(iso)} />
              </>
            )}
          </BuilderSection>

          <BuilderSection className="space-y-3">
            {!(loanSecurity && (lane.method === 'PRO_RATA' || (lane.method === 'SIZE_TO_CAPACITY' && lane.weightBasis === 'CAPACITY'))) && <ModalButton style={{ width: '100%' }} onClick={() => setShowTargetModal(true)}>Set Portfolio Targets</ModalButton>}
            {!loanSecurity && selectedSecurities.length > 0 && getActivePortfolios().length > 0 && !aiSuggestion && (
              <AIButton variant="primary" style={{ width: '100%' }} onClick={() => getAISuggestion()} disabled={aiSuggestLoading}>
                {aiSuggestLoading ? '✨ Analyzing...' : '✨ AI Suggest Allocation'}
              </AIButton>
            )}
            <ExecuteButton onClick={loanSecurity ? lane.analyze : executeTrade} disabled={loading || !!lane.busy || (!!loanSecurity && !lane.ready)} $loading={loading || !!lane.busy}>
              {lane.busy ? lane.busy : loading ? 'Executing...' : `Execute ${direction.toUpperCase()} Analysis`}
            </ExecuteButton>
          </BuilderSection>
        </BuilderPanel>

        <ResultsPanel>
          {!loanSecurity && durationGap && renderDurationGap()}
          {loanSecurity && lane.error && (
            <ErrorBanner>
              <span>{lane.error}</span>
              <button onClick={() => lane.setError(null)} style={{ background: 'none', border: 'none', color: '#ff4757', cursor: 'pointer' }}>x</button>
            </ErrorBanner>
          )}
          {loanSecurity && !lane.analysis ? (
            renderNextSteps()
          ) : loanSecurity && lane.analysis ? (() => {
            const a = lane.analysis;
            const passed = lane.funded.reduce((n, p) => n + p.rules.filter(r => r.passed).length, 0);
            const total = lane.funded.reduce((n, p) => n + p.rules.length, 0);
            const laneActions = (
              <ActionButtonsContainer>
                {!lane.explanation && <ActionButton variant="secondary" onClick={lane.explain} disabled={!!lane.busy}>✨ Explain this sizing</ActionButton>}
                {!lane.tickets && lane.funded.length > 0 && <ActionButton variant="primary" onClick={lane.createTrade} disabled={!!lane.busy}>✓ Create Trade</ActionButton>}
                {!lane.saved && <ActionButton variant="secondary" onClick={lane.save} disabled={!!lane.busy}>Save analysis only</ActionButton>}
                {lane.saved && <ActionButton variant="secondary" onClick={lane.checkFreshness} disabled={!!lane.busy}>Check freshness</ActionButton>}
                {lane.saved && <ActionButton variant="secondary" onClick={() => lane.exportRun('csv')}>CSV</ActionButton>}
                {lane.saved && <ActionButton variant="secondary" onClick={() => lane.exportRun('json')}>JSON</ActionButton>}
                <ActionButton variant="secondary" onClick={handleNewTrade} disabled={!!lane.busy}>➕ New Trade</ActionButton>
              </ActionButtonsContainer>
            );
            return (
              <ResultCard>
                <ResultHeader>
                  <div>
                    <ResultTitle>{lane.tickets ? 'Trade Created' : 'Managed CLO Sizing'}</ResultTitle>
                    <ResultSubtitle>
                      {a.cusip} · {a.method.replace(/_/g, ' ').toLowerCase()} · trade {a.tradeDate} · settle {a.settleDate}{a.settlementConvention ? ` (${a.settlementConvention.replace(/_/g, ' ')})` : ''} · {Number(a.cleanPrice).toFixed(3)} clean
                      {lane.saved && <> · saved {lane.saved.id.slice(0, 8)} by {lane.saved.createdBy}{lane.saved.existing ? ' (already saved with these inputs)' : ''}</>}
                    </ResultSubtitle>
                  </div>
                </ResultHeader>
                {laneActions}
                {lane.freshness && (
                  <div className={cn('mx-6 mb-3 rounded-lg px-3 py-2 text-xs border', lane.freshness.stale ? 'bg-amber-500/10 text-amber-300 border-amber-500/30' : 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20')}>
                    {lane.freshness.stale ? 'Analysis stale: ' : 'Analysis current: '}
                    {(lane.freshness.portfolios || []).map((p: any) => `${p.portfolioName}: ${p.stale ? (Array.isArray(p.changed) && p.changed.length ? p.changed.join(', ') + ' changed' : 'moved') : 'unchanged'}`).join(' · ')} (checked {String(lane.freshness.checkedAt).slice(0, 19)})
                  </div>
                )}
                <ImpactGrid>
                  <ImpactCell><ImpactValue>{parShort(a.allocatedPar)}</ImpactValue><ImpactLabel>Allocated</ImpactLabel></ImpactCell>
                  <ImpactCell><ImpactValue negative={a.residualPar > 0}>{parShort(a.residualPar)}</ImpactValue><ImpactLabel>Residual</ImpactLabel></ImpactCell>
                  <ImpactCell><ImpactValue>{money(a.totals.total)}</ImpactValue><ImpactLabel>Required cash</ImpactLabel></ImpactCell>
                  <ImpactCell><ImpactValue positive={total > 0 && passed === total} negative={passed < total}>{passed}/{total}</ImpactValue><ImpactLabel>Tests pass</ImpactLabel></ImpactCell>
                </ImpactGrid>
                {a.residualPar > 0 && <div className="px-6 pb-2 text-xs text-amber-300">{parShort(a.residualPar)} of the request has no CLO that can take it under its tests.</div>}

                {/* One line per CLO: state, proposed par against capacity, the binding test. */}
                <div className="px-6 pb-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                  {a.portfolios.map(p => {
                    const cap = p.capacity.maximum ?? 0;
                    const pct = cap > 0 ? Math.min(100, Math.round((p.proposedPar / cap) * 100)) : 0;
                    return (
                      <div key={p.portfolioId} className="rounded-xl border border-white/5 bg-slate-900/60 p-3">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-sm font-bold text-white">{p.portfolioName}</div>
                          <span className={cn('px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider',
                            p.state === 'CLEAR' ? 'bg-emerald-500/10 text-emerald-400' : p.state === 'BREACH' ? 'bg-red-500/10 text-red-400' : p.state === 'NOT_MANAGED' || p.state === 'UNVALIDATED' || p.state === 'NO_CAPACITY' ? 'bg-slate-500/10 text-slate-400' : 'bg-amber-500/10 text-amber-400')}>{stateText[p.state] || p.state}</span>
                        </div>
                        <div className="mt-2 h-2 rounded-full bg-white/5 overflow-hidden" title={`${parShort(p.proposedPar)} of ${p.capacity.maximum == null ? 'no assessable' : parShort(p.capacity.maximum)} capacity`}>
                          <div className={cn('h-full rounded-full', p.state === 'BREACH' ? 'bg-red-400' : pct >= 100 ? 'bg-amber-400' : 'bg-indigo-400')} style={{ width: `${p.capacity.maximum == null ? 0 : pct}%` }} />
                        </div>
                        <div className="mt-1.5 flex justify-between text-[11px] text-slate-400">
                          <span>{parShort(p.proposedPar)} of {p.capacity.maximum == null ? p.capacity.status.replace(/_/g, ' ').toLowerCase() : parShort(p.capacity.maximum)}</span>
                          <span className="text-right">{p.capacity.binding.length > 0 ? `binds: ${p.capacity.binding[0].replace(/^CLO: /, '')}` : p.capacity.reasons[0] ? p.capacity.reasons[0].slice(0, 60) : 'no test binds'}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {lane.explanation && (
                  <div className="mx-6 mb-4 rounded-xl border border-indigo-500/20 bg-indigo-500/5 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-indigo-300">Why this sizing</div>
                      <div className="text-[10px] text-slate-500">{lane.explanation.llm ? `prose by ${lane.explanation.llm}; numbers from the engine` : `engine facts only${lane.explanation.llmError ? ` (LLM: ${lane.explanation.llmError})` : ''}`}</div>
                    </div>
                    {lane.explanation.narrative && <div className="mt-2 text-sm text-slate-200 whitespace-pre-line leading-relaxed">{lane.explanation.narrative}</div>}
                    <ul className="mt-3 space-y-1 text-xs text-slate-400 list-disc pl-4">
                      {lane.explanation.facts.map((f, i) => <li key={i} className={cn(f.kind === 'breach' && 'text-red-300', f.kind === 'unknown' && 'text-amber-300', f.kind === 'tightest' && 'text-slate-200')}>{f.text}</li>)}
                    </ul>
                  </div>
                )}

                {/* The trade as it will be booked: one block, one allocation per funded CLO. */}
                <ComparisonContainer>
                  <div className="px-0 pb-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    {a.bookingMode === 'BLOCK' ? `Proposed trade · one LL block, ${a.fundedPortfolios} allocations` : a.bookingMode === 'SINGLE' ? 'Proposed trade · one LL ticket' : 'Proposed trade · nothing funded'}
                    {a.bookingMode === 'BLOCK' ? ` · assignment fee ${money(a.assignmentFee)} on each allocation` : ''}
                    {a.weightBasis === 'CAPACITY' ? ' · split by capacity' : a.weightBasis === 'TARGET' ? ' · split by your weights' : ''}
                  </div>
                  <ComparisonHeader>
                    <div>Portfolio</div>
                    <div style={{ textAlign: 'right' }}>Par</div>
                    <div style={{ textAlign: 'right' }}>Clean + accrued</div>
                    <div style={{ textAlign: 'right' }}>Fee</div>
                    <div style={{ textAlign: 'right' }}>Total cash</div>
                    <div style={{ textAlign: 'right' }}>Tests</div>
                  </ComparisonHeader>
                  {a.portfolios.map(p => (
                    <ComparisonRow key={p.portfolioId}>
                      <PortfolioName>{p.portfolioName}
                        {p.dealName ? <span className="block text-[10px] font-normal text-slate-500">{p.dealName} · {p.noteClasses || 0} note classes</span> : null}
                        {p.managed && <span className={cn('block text-[10px] font-normal', p.legalBuyer ? 'text-slate-500' : 'text-amber-300')}>{p.legalBuyer ? `Buyer of record: ${p.legalBuyer}${p.custody ? ` · ${p.custody}` : ''}` : 'No legal buyer on the linked deal; the ticket will carry none'}</span>}
                      </PortfolioName>
                      <AllocatedAmount>{parShort(p.proposedPar)}</AllocatedAmount>
                      <DurationValue>{money(p.economics.clean + p.economics.accrued)}</DurationValue>
                      <DurationValue>{money(p.economics.fee)}</DurationValue>
                      <DurationValue>{money(p.economics.total)}</DurationValue>
                      <div style={{ textAlign: 'right' }} title={[...p.errors, ...p.warnings].join('\n')}>
                        <span className={cn('px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider',
                          p.state === 'CLEAR' ? 'bg-emerald-500/10 text-emerald-400' : p.state === 'BREACH' ? 'bg-red-500/10 text-red-400' : p.state === 'NOT_MANAGED' || p.state === 'UNVALIDATED' || p.state === 'NO_CAPACITY' ? 'bg-slate-500/10 text-slate-400' : 'bg-amber-500/10 text-amber-400')}>
                          {stateText[p.state] || p.state}
                        </span>
                      </div>
                    </ComparisonRow>
                  ))}
                </ComparisonContainer>

                {/* Per CLO: capacity, cash, and the full indenture matrix. */}
                <div className="px-6 pb-4 space-y-3">
                  {a.portfolios.map(p => {
                    const open = cloDetailOpen[p.portfolioId] ?? (p.state !== 'CLEAR');
                    const passing = p.rules.filter(r => r.passed).length;
                    return (
                    <div key={p.portfolioId} className="rounded-xl border border-white/5 bg-slate-950/40 p-4">
                      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3 cursor-pointer" onClick={() => setCloDetailOpen(prev => ({ ...prev, [p.portfolioId]: !open }))}>
                        <div>
                          <div className="text-sm font-bold text-white">{open ? '▾' : '▸'} {p.portfolioName} <span className="text-[11px] font-normal text-slate-500">· {p.rules.length} tests · {passing === p.rules.length ? 'all pass' : `${passing} pass, ${p.rules.length - passing} open or failing`}</span></div>
                          <div className="text-[11px] text-slate-500 mt-0.5">
                            Capacity {p.capacity.maximum == null ? p.capacity.status.replace(/_/g, ' ').toLowerCase() : parShort(p.capacity.maximum)}
                            {p.capacity.binding.length > 0 && ` · binding ${p.capacity.binding.join(', ')}`}
                            {p.capacity.nextSize != null && p.capacity.nextFailures.length > 0 && ` · ${parShort(p.capacity.nextSize)} fails ${p.capacity.nextFailures.join(', ')}`}
                            {p.capacity.reasons.length > 0 && ` · ${p.capacity.reasons.join('; ')}`}
                          </div>
                        </div>
                        <div className="text-right text-[11px] text-slate-400 whitespace-nowrap">Cash {money(p.cashBefore)} → <span className="text-white font-semibold">{money(p.cashAfter)}</span></div>
                      </div>
                      {p.issues.length > 0 && <div className="mt-2 text-xs text-amber-300">{p.issues.join(' · ')}</div>}
                      {p.errors.length > 0 && <div className="mt-2 text-xs text-red-300">{p.errors.join(' · ')}</div>}
                      {(p.missingTests?.length || 0) > 0 && <div className="mt-2 text-xs text-amber-300">Rulebook incomplete: this CLO has no rule for {p.missingTests!.join(', ')}. Those tests are not being run; add them on Compliance Rules.</div>}
                      {(p.disabledTests?.length || 0) > 0 && <div className="mt-2 text-xs text-slate-400">Switched off on this CLO: {p.disabledTests!.join(', ')}.</div>}
                      {(p.draftCount || 0) > 0 && <div className="mt-2 text-xs text-amber-300">{money(p.draftPar)} in {p.draftCount} draft ticket{p.draftCount === 1 ? '' : 's'} on this CLO is not counted yet. A purchase holds capacity from the moment it is submitted for pre-clear, so whichever is submitted first takes the room.</div>}
                      {open && p.rules.length > 0 && (
                        <div className="mt-3">
                          <div className="grid grid-cols-[1.6fr_0.8fr_0.8fr_0.8fr_0.7fr_2fr] gap-x-3 text-[10px] font-bold uppercase tracking-wider text-slate-500 pb-2 border-b border-white/5">
                            <div>Test</div><div className="text-right">Before</div><div className="text-right">After</div><div className="text-right">Limit</div><div className="text-right">Result</div><div>Note</div>
                          </div>
                          {(cloMatrixOpen[p.portfolioId] ? p.rules : p.rules.filter(r => r.ruleKey.startsWith('clo_') || !r.passed)).map((r, i) => {
                            const drillKey = `${p.portfolioId}:${r.ruleId}`;
                            const detail = lane.drill[drillKey];
                            const canDrill = r.ruleKey.startsWith('clo_') && r.ruleId != null;
                            const classSuffix = r.className && !r.ruleName.includes(r.className) ? ` · ${r.className}` : '';
                            return (
                            <React.Fragment key={`${r.ruleId}-${i}`}>
                            <div className="grid grid-cols-[1.6fr_0.8fr_0.8fr_0.8fr_0.7fr_2fr] gap-x-3 items-start text-xs py-2 border-b border-white/5">
                              <div className="text-slate-200 font-semibold">
                                {canDrill
                                  ? <button className="text-left hover:text-indigo-200" title="Show what this number is made of" onClick={() => lane.toggleDrill(p.portfolioId, r.ruleId, p.proposedPar)}>{detail ? '▾' : '▸'} {r.ruleName}{classSuffix}</button>
                                  : <>{r.ruleName}{classSuffix}</>}
                              </div>
                              <div className="text-right text-slate-400 font-mono">{showValue(r.before, r.unit)}</div>
                              <div className="text-right text-white font-mono">{showValue(r.after, r.unit)}</div>
                              <div className="text-right text-slate-400 font-mono">{showValue(r.limit, r.unit)}</div>
                              <div className="text-right">
                                <span className={cn('px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider', r.passed ? 'bg-emerald-500/10 text-emerald-400' : r.status === 'UNKNOWN' ? 'bg-amber-500/10 text-amber-400' : r.status === 'NOT_APPLICABLE' ? 'bg-slate-500/10 text-slate-400' : r.breachAction === 'BLOCK' ? 'bg-red-500/10 text-red-400' : 'bg-amber-500/10 text-amber-400')}>{rowResult(r)}</span>
                              </div>
                              <div className="text-slate-400 leading-snug">
                                {r.missing.length > 0 ? <span className="text-amber-300">Needs {r.missing.join(', ')}. </span> : null}{r.message}
                                {r.calculation && <div className="text-[10px] text-slate-600 mt-0.5">{r.calculation}</div>}
                              </div>
                            </div>
                            {detail && (
                              <div className="my-2 ml-4 rounded-lg border border-indigo-500/20 bg-indigo-500/5 p-3">
                                {detail === 'loading' ? <div className="text-xs text-slate-400">Loading what this number is made of…</div>
                                  : 'error' in detail ? <div className="text-xs text-red-300">{detail.error}</div>
                                  : <>
                                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                                      <div className="text-[10px] font-bold uppercase tracking-wider text-indigo-300">{detail.title}</div>
                                      {detail.coverage && <div className="text-[10px] text-slate-500">{detail.coverage}</div>}
                                    </div>
                                    <table className="w-full mt-2 text-xs">
                                      <thead><tr>{detail.columns.map(col => <th key={col.key} className={cn('pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-500', col.unit === 'text' ? 'text-left' : 'text-right')}>{col.label}</th>)}</tr></thead>
                                      <tbody>{detail.rows.map((row, ri) => (
                                        <tr key={ri} className="border-t border-white/5">{detail.columns.map(col => {
                                          const v = row[col.key];
                                          const shown = v === null || v === undefined || v === '' ? '' : typeof v === 'string' ? v
                                            : col.unit === 'count' ? String(v) : col.unit === 'fraction' ? (Number(v) * 100).toFixed(2) + '%' : showValue(Number(v), col.unit);
                                          return <td key={col.key} className={cn('py-1', col.unit === 'text' ? 'text-left text-slate-200' : 'text-right font-mono text-slate-300', String(row.name || '').includes('(this loan)') && 'text-indigo-200')}>{shown}</td>;
                                        })}</tr>
                                      ))}</tbody>
                                    </table>
                                  </>}
                              </div>
                            )}
                            </React.Fragment>
                            );
                          })}
                          {p.rules.some(r => !r.ruleKey.startsWith('clo_') && r.passed) && (
                            <button className="text-xs text-indigo-300 hover:text-indigo-200 mt-2" onClick={() => setCloMatrixOpen(prev => ({ ...prev, [p.portfolioId]: !prev[p.portfolioId] }))}>
                              {cloMatrixOpen[p.portfolioId] ? 'Hide the other passing rules' : `Show ${p.rules.filter(r => !r.ruleKey.startsWith('clo_') && r.passed).length} other passing rules`}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>

                {lane.tickets && (
                  <div className="mx-6 mb-4 rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <div className="text-sm font-bold text-white">
                          {lane.parent ? `LL block #${lane.parent.tradeId} · ${lane.parent.status.replace(/_/g, ' ')} · ${lane.allocations.length} allocation${lane.allocations.length === 1 ? '' : 's'}` : `${lane.allocations.length} ticket${lane.allocations.length === 1 ? '' : 's'}`}
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5">{lane.parent ? 'Open the block ticket to submit the allocations for pre-clear, approve, send and execute; each allocation clears against its own CLO.' : 'Open the ticket to submit it for pre-clear.'}</div>
                      </div>
                      <div className="flex gap-2">
                        {lane.parent && <ActionButton variant="primary" onClick={() => navigate('/trade-entry/leveraged-loan', { state: { transactionId: lane.parent!.tradeId } })}>Open block #{lane.parent.tradeId}</ActionButton>}
                        <ActionButton variant="secondary" onClick={() => navigate(`/bond-transactions?query=${encodeURIComponent('builder-run:' + lane.saved?.id)}`)}>View in Blotter</ActionButton>
                      </div>
                    </div>
                    <div className="mt-3 space-y-1.5">
                      {lane.allocations.map(t => (
                        <div key={t.tradeId} className="grid grid-cols-[1fr_auto_auto_auto] gap-4 items-center text-xs rounded-lg bg-slate-950/40 border border-white/5 px-3 py-2">
                          <div className="font-semibold text-slate-200">{t.portfolioName || `Portfolio ${t.portfolioId}`}</div>
                          <div className="text-slate-300">{parShort(t.par)}</div>
                          <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-amber-500/10 text-amber-400">{t.status.replace(/_/g, ' ')}</span>
                          <button className="font-mono text-indigo-300 hover:text-indigo-200" onClick={() => navigate('/trade-entry/leveraged-loan', { state: { transactionId: t.tradeId } })}>#{t.tradeId}</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {laneActions}
              </ResultCard>
            );
          })() : createdOrders ? (
            <ResultCard>
              <ResultHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <ResultTitle>Orders Created</ResultTitle>
                    <ResultSubtitle>{createdOrders.length} tickets in DRAFT status</ResultSubtitle>
                  </div>
                </div>
              </ResultHeader>

              <div className="px-6 pb-2">
                <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-x-4 gap-y-0 text-[10px] font-bold uppercase tracking-wider text-slate-500 pb-2 border-b border-white/5">
                  <div>Portfolio</div>
                  <div className="text-right">Amount</div>
                  <div className="text-right">Type</div>
                  <div className="text-right">Status</div>
                  <div className="text-right">Ticket ID</div>
                </div>
              </div>

              <div className="px-6 pb-6 space-y-2">
                {createdOrders.map((order: any, idx: number) => (
                  <div key={order.id || idx} className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-x-4 items-center py-3 px-4 rounded-xl bg-slate-950/40 border border-white/5 hover:border-emerald-500/20 transition-all">
                    <div>
                      <div className="text-sm font-bold text-white">{order.portfolioName || `Portfolio ${order.portfolioId}`}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5">{order.cusip || order.securityName || ''}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-white">${((order.amount || 0) / 1000000).toFixed(2)}M</div>
                      <div className="text-[10px] text-slate-500">{order.tradeDate}</div>
                    </div>
                    <div className="text-right">
                      <span className={cn(
                        "px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider",
                        order.transactionType === 'BUY' ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400"
                      )}>
                        {order.transactionType}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-amber-500/10 text-amber-400">
                        {order.status || 'DRAFT'}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="text-xs font-mono text-slate-400">#{order.id}</span>
                    </div>
                  </div>
                ))}
              </div>

              <ActionButtonsContainer>
                <ActionButton variant="primary" onClick={() => navigate('/bond-transactions')}>
                  <ExternalLink className="w-4 h-4 inline mr-1" style={{ verticalAlign: 'middle' }} />
                  View in Blotter
                </ActionButton>
                <ActionButton variant="secondary" onClick={handleNewTrade}>
                  <Plus className="w-4 h-4 inline mr-1" style={{ verticalAlign: 'middle' }} />
                  New Trade
                </ActionButton>
              </ActionButtonsContainer>
            </ResultCard>
          ) : results ? (
            <ResultCard>
              <ResultHeader>
                <ResultTitle>Impact Analysis</ResultTitle>
                <ResultSubtitle>{results.portfolios.length} portfolios affected</ResultSubtitle>
              </ResultHeader>
              <ImpactGrid>
                <ImpactCell><ImpactValue>{results.portfolios.length}</ImpactValue><ImpactLabel>Portfolios</ImpactLabel></ImpactCell>
                <ImpactCell><ImpactValue>${(results.totals.valChange / 1000000).toFixed(2)}M</ImpactValue><ImpactLabel>Value Δ</ImpactLabel></ImpactCell>
                <ImpactCell><ImpactValue positive={results.totals.durChange >= 0} negative={results.totals.durChange < 0}>{results.totals.durChange >= 0 ? '+' : ''}{results.totals.durChange.toFixed(4)}</ImpactValue><ImpactLabel>Dur Δ</ImpactLabel></ImpactCell>
                <ImpactCell><ImpactValue>{(() => {
                  const rows = (results.portfolios || []).filter((p: any) => p.liquidityPercentAfter != null && p.afterVal > 0);
                  if (!rows.length) return '—';
                  const totalMv = rows.reduce((a: number, p: any) => a + p.afterVal, 0);
                  const weighted = rows.reduce((a: number, p: any) => a + p.liquidityPercentAfter * p.afterVal, 0) / totalMv;
                  return `${weighted.toFixed(0)}%`;
                })()}</ImpactValue><ImpactLabel>Liquidity</ImpactLabel></ImpactCell>
              </ImpactGrid>
              <ComparisonContainer>
                <ComparisonHeader>
                  <div>Portfolio</div>
                  <div style={{ textAlign: 'right' }}>Allocated</div>
                  <div style={{ textAlign: 'right' }}>Before Dur</div>
                  <div style={{ textAlign: 'right' }}>After Dur</div>
                  <div style={{ textAlign: 'right' }}>Δ Dur</div>
                  <div style={{ textAlign: 'right' }}>Status</div>
                </ComparisonHeader>
                {results.portfolios.map((p: any) => (
                  <ComparisonRow key={p.id}>
                    <PortfolioName>{p.name}</PortfolioName>
                    <AllocatedAmount>${(p.allocatedAmount / 1000000).toFixed(2)}M</AllocatedAmount>
                    <DurationValue>{p.beforeDur.toFixed(4)}</DurationValue>
                    <DurationValue>{p.afterDur.toFixed(4)}</DurationValue>
                    <div style={{ textAlign: 'right' }}><ChangeValue positive={p.afterDur >= p.beforeDur}>{p.afterDur >= p.beforeDur ? '+' : ''}{(p.afterDur - p.beforeDur).toFixed(4)}</ChangeValue></div>
                    <div style={{ textAlign: 'right' }} title={(p.complianceErrors || []).join('\n')}>
                      {p.complianceReady === true ? (
                        <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-emerald-500/10 text-emerald-400">Pass</span>
                      ) : p.complianceReady === false ? (
                        <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-amber-500/10 text-amber-400">Findings</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-slate-500/10 text-slate-400">Unvalidated</span>
                      )}
                    </div>
                  </ComparisonRow>
                ))}
              </ComparisonContainer>
              <ActionButtonsContainer>
                <ActionButton variant="primary" onClick={handleCreateOrder} disabled={loading}>✓ Create Order</ActionButton>
                <ActionButton variant="secondary" onClick={handleNewTrade} disabled={loading}>➕ New Trade</ActionButton>
              </ActionButtonsContainer>
            </ResultCard>
          ) : (
            renderNextSteps()
          )}
        </ResultsPanel>
      </ContentGrid>}

      <ModalBackdrop show={showSecurityModal}>
        <ModalCard>
          <ModalHeader>
            <ModalTitle>Add Security</ModalTitle>
            <ModalClose onClick={() => setShowSecurityModal(false)}>×</ModalClose>
          </ModalHeader>
          <ModalBody>
            <InputGroup>
              <InputLabel>Search by CUSIP, ticker or name</InputLabel>
              <StyledInput value={searchCusip} onChange={e => setSearchCusip(e.target.value)} onKeyDown={e => e.key === 'Enter' && searchSecurity()} placeholder="Start typing…" autoFocus />
            </InputGroup>
            {securitySuggestions.length > 0 && (
              <div className="mt-2 max-h-64 overflow-y-auto rounded-xl border border-white/10 bg-slate-950/60 divide-y divide-white/5">
                {securitySuggestions.map((sec: any) => (
                  <button key={sec.id || sec.cusip} onClick={() => pickSecurity(sec)} className="w-full text-left px-3 py-2 hover:bg-white/5 transition-colors">
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-mono text-xs text-blue-300">{sec.cusip}</span>
                      <span className="text-[10px] uppercase tracking-wider text-slate-500">{String(sec.securityType || sec.assetClassName || '').replace(/_/g, ' ')}</span>
                    </div>
                    <div className="text-xs text-slate-200 truncate">{sec.securityName || sec.description}</div>
                    <div className="text-[10px] text-slate-500">{sec.issuerName || sec.issuer || ''}{sec.maturityDate ? ` · matures ${sec.maturityDate}` : ''}{sec.currentPrice ? ` · ${Number(sec.currentPrice).toFixed(3)}` : ''}</div>
                  </button>
                ))}
              </div>
            )}
            {searchCusip.trim().length >= 2 && securitySuggestions.length === 0 && <div className="mt-2 text-xs text-slate-500">No match yet. Enter a full CUSIP and press Add Security to look it up directly.</div>}
          </ModalBody>
          <ModalFooter>
            <ModalButton onClick={() => setShowSecurityModal(false)}>Cancel</ModalButton>
            <ModalButton primary onClick={searchSecurity}>Add Security</ModalButton>
          </ModalFooter>
        </ModalCard>
      </ModalBackdrop>

      <ModalBackdrop show={showGroupManager}>
        <ModalCard style={{ maxWidth: 800 }}>
          <ModalHeader>
            <ModalTitle>Manage Portfolio Groups</ModalTitle>
            <ModalClose onClick={() => setShowGroupManager(false)}>×</ModalClose>
          </ModalHeader>
          <ModalBody>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Form Side */}
              <div className="space-y-4 p-4 rounded-xl bg-white/5 border border-white/10">
                <h4 className="text-sm font-bold text-blue-400 uppercase tracking-wider">
                  {editingGroup ? 'Edit Group' : 'Create New Group'}
                </h4>
                <InputGroup>
                  <InputLabel>Group Name</InputLabel>
                  <StyledInput 
                    value={groupForm.groupName} 
                    onChange={e => setGroupForm({ ...groupForm, groupName: e.target.value })}
                    placeholder="e.g., Core Fixed Income"
                  />
                </InputGroup>
                <InputGroup>
                  <InputLabel>Description</InputLabel>
                  <StyledInput 
                    value={groupForm.description} 
                    onChange={e => setGroupForm({ ...groupForm, description: e.target.value })}
                    placeholder="Optional description..."
                  />
                </InputGroup>
                <div className="flex gap-2">
                  <ModalButton primary onClick={editingGroup ? handleUpdateGroup : handleCreateGroup} disabled={!groupForm.groupName}>
                    {editingGroup ? 'Save Changes' : 'Create Group'}
                  </ModalButton>
                  {editingGroup && (
                    <ModalButton onClick={() => { setEditingGroup(null); setGroupForm({ groupName: '', description: '' }); }}>
                      Cancel
                    </ModalButton>
                  )}
                </div>
              </div>

              {/* List Side */}
              <div className="space-y-3">
                <h4 className="text-sm font-bold text-slate-400 uppercase tracking-wider px-1">Existing Groups</h4>
                <div className="max-h-96 overflow-y-auto space-y-2 pr-2">
                  {portfolioGroups.map(g => (
                    <div key={g.id} className="p-3 rounded-xl bg-slate-900/50 border border-white/5 group hover:border-blue-500/30 transition-all">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-bold text-white">{g.groupName}</div>
                          <div className="text-[10px] text-slate-500 mt-1 uppercase tracking-widest">
                            {g.portfolios?.length || 0} Portfolios
                          </div>
                        </div>
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => { setEditingGroup(g); setGroupForm({ groupName: g.groupName, description: g.description }); }} className="p-1.5 rounded-lg hover:bg-blue-500/20 text-blue-400"><Layers className="w-3.5 h-3.5" /></button>
                          <button onClick={() => handleDeleteGroup(g.id)} className="p-1.5 rounded-lg hover:bg-red-500/20 text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                      
                      {/* Portfolios List */}
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {g.portfolios?.map((p: any) => (
                          <span key={p.id} className="px-2 py-0.5 rounded bg-white/5 text-[9px] text-slate-400 border border-white/5 flex items-center gap-1">
                            {p.portfolioName}
                            <button onClick={() => handleRemovePortfolioFromGroup(g.id, p.id)} className="hover:text-red-400">×</button>
                          </span>
                        ))}
                        <StyledSelect
                          value=""
                          onChange={e => e.target.value && handleAddPortfoliosToGroup(g.id, [parseInt(e.target.value)])}
                          className="!w-auto !py-0.5 !px-2 !text-[9px] !h-auto !bg-blue-500/10 !border-blue-500/20 !text-blue-400"
                        >
                          <option value="">+ Add</option>
                          {portfolios.filter(p => !g.portfolios?.some((gp: any) => gp.id === p.id)).map(p => (
                            <option key={p.id} value={p.id}>{p.portfolioName}</option>
                          ))}
                        </StyledSelect>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <ModalButton primary onClick={() => setShowGroupManager(false)}>Done</ModalButton>
          </ModalFooter>
        </ModalCard>
      </ModalBackdrop>

      <ModalBackdrop show={showTargetModal}>
        <ModalCard style={{ maxWidth: 500 }}>
          <ModalHeader>
            <ModalTitle>Set Portfolio Targets</ModalTitle>
            <ModalClose onClick={() => setShowTargetModal(false)}>×</ModalClose>
          </ModalHeader>
          <ModalBody>
            {loanSecurity ? (
              <>
                <div className="text-[11px] text-slate-500 mb-3">
                  {lane.method === 'ABSOLUTE' ? 'Par per CLO (M thousands, MM millions).' : lane.method === 'SIZE_TO_CAPACITY' ? 'Target weight per CLO; the requested par is split by weight, capped at each CLO’s capacity.' : 'Pro rata splits by collateral par; no targets needed.'}
                </div>
                <TargetTable>
                  {lane.managed.filter(p => lane.selected.includes(p.portfolioId)).map(p => (
                    <TargetRow key={p.portfolioId}>
                      <TargetCell>{p.portfolioName}</TargetCell>
                      <TargetCell>
                        {lane.method === 'ABSOLUTE'
                          ? <TargetInput value={lane.line(p.portfolioId).par} onChange={e => lane.setLine(p.portfolioId, 'par', e.target.value)} onBlur={e => lane.setLine(p.portfolioId, 'par', replaceShorthandOnBlur(e.target.value))} placeholder="2MM" />
                          : <TargetInput value={lane.line(p.portfolioId).weight} onChange={e => lane.setLine(p.portfolioId, 'weight', e.target.value)} placeholder="1" disabled={lane.method === 'PRO_RATA'} />}
                      </TargetCell>
                    </TargetRow>
                  ))}
                </TargetTable>
              </>
            ) : (
              <TargetTable>
                {getActivePortfolios().map((p: any) => (
                  <TargetRow key={p.id}>
                    <TargetCell>{p.portfolioName}</TargetCell>
                    <TargetCell>
                      <TargetInput value={targets[p.id] || ''} onChange={e => setTargets({ ...targets, [p.id]: e.target.value })} placeholder="0.00" />
                    </TargetCell>
                  </TargetRow>
                ))}
              </TargetTable>
            )}
          </ModalBody>
          <ModalFooter>
            <ModalButton onClick={() => setTargets({})}>Clear All</ModalButton>
            <ModalButton primary onClick={() => setShowTargetModal(false)}>Done</ModalButton>
          </ModalFooter>
        </ModalCard>
      </ModalBackdrop>

      <LoadingOverlay isVisible={blockingOperation.isActive} message={blockingOperation.message} subMessage={blockingOperation.subMessage} />
    </PageContainer>
  );
};

// ==================== ALTERNATIVES VIEW SUB-COMPONENT ====================

interface AlternativesViewProps {
  portfolios: any[];
  sleeveTargets: Record<string, number>;
  setSleeveTargets: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  selectedSleeve: string | null;
  setSelectedSleeve: React.Dispatch<React.SetStateAction<string | null>>;
  hoveredSleeve: string | null;
  setHoveredSleeve: React.Dispatch<React.SetStateAction<string | null>>;
  navigate: ReturnType<typeof useNavigate>;
}

const AlternativesView: React.FC<AlternativesViewProps> = ({
  portfolios,
  sleeveTargets,
  setSleeveTargets,
  selectedSleeve,
  setSelectedSleeve,
  hoveredSleeve,
  setHoveredSleeve,
  navigate,
}) => {
  // Compute current allocations from portfolio positions
  const currentAllocations = useMemo(() => {
    const bucketValues: Record<string, number> = {};
    SLEEVE_BUCKETS.forEach(b => { bucketValues[b.key] = 0; });

    let totalValue = 0;

    portfolios.forEach((p: any) => {
      const positions = p.positions || [];
      positions.forEach((pos: any) => {
        const mv = parseFloat(pos.marketValue || pos.currentValue || 0);
        const bucketKey = mapToSleeveBucket(pos.assetClassName || pos.assetClass, pos.securityType);
        bucketValues[bucketKey] += mv;
        totalValue += mv;
      });

      // Positions are loaded from /api/positions/latest and attached to portfolios.
      // If a portfolio has no positions, it contributes nothing to the allocation.
    });

    // Convert to percentages
    const result: Record<string, number> = {};
    SLEEVE_BUCKETS.forEach(b => {
      result[b.key] = totalValue > 0 ? (bucketValues[b.key] / totalValue) * 100 : 0;
    });
    return result;
  }, [portfolios]);

  const getDriftSeverity = (drift: number): 'green' | 'amber' | 'red' => {
    const absDrift = Math.abs(drift);
    if (absDrift <= 2) return 'green';
    if (absDrift <= 5) return 'amber';
    return 'red';
  };

  const getSleeveAction = (bucket: typeof SLEEVE_BUCKETS[0], drift: number): React.ReactNode => {
    const absDrift = Math.abs(drift);
    if (absDrift <= 2) {
      return <span style={{ color: '#10b981' }}>On target</span>;
    }

    const route = SLEEVE_ROUTES[bucket.key] || '/trade-entry';
    const direction = drift > 0 ? 'Over-weight' : 'Under-weight';

    return (
      <span>
        {direction} {absDrift.toFixed(1)}% &mdash;{' '}
        <ActionLink onClick={() => navigate(route)}>View {bucket.label}</ActionLink>
      </span>
    );
  };

  const totalTarget = Object.values(sleeveTargets).reduce((s, v) => s + v, 0);
  const totalCurrent = Object.values(currentAllocations).reduce((s, v) => s + v, 0);

  return (
    <AlternativesContainer>
      <SleeveGrid>
        <SleeveGridHeader>
          <SleeveGridTitle>Sleeve Target Allocation</SleeveGridTitle>
          <SleeveGridSubtitle>Multi-asset allocation across public and private markets</SleeveGridSubtitle>
        </SleeveGridHeader>

        <SleeveTableContainer>
          <SleeveTableRow $isHeader>
            <SleeveTableHeaderCell>Sleeve</SleeveTableHeaderCell>
            <SleeveTableHeaderCell style={{ textAlign: 'right' }}>Target %</SleeveTableHeaderCell>
            <SleeveTableHeaderCell style={{ textAlign: 'right' }}>Current %</SleeveTableHeaderCell>
            <SleeveTableHeaderCell style={{ textAlign: 'right' }}>Drift</SleeveTableHeaderCell>
            <SleeveTableHeaderCell>Status</SleeveTableHeaderCell>
          </SleeveTableRow>

          {SLEEVE_BUCKETS.map(bucket => {
            const target = sleeveTargets[bucket.key] || 0;
            const current = currentAllocations[bucket.key] || 0;
            const drift = current - target;
            const severity = getDriftSeverity(drift);

            return (
              <SleeveTableRow
                key={bucket.key}
                onClick={() => setSelectedSleeve(selectedSleeve === bucket.key ? null : bucket.key)}
                onMouseEnter={() => setHoveredSleeve(bucket.key)}
                onMouseLeave={() => setHoveredSleeve(null)}
                style={{
                  background: selectedSleeve === bucket.key ? 'rgba(59, 130, 246, 0.06)' : undefined,
                }}
              >
                <SleeveNameCell>
                  <SleeveColorDot color={bucket.color} />
                  <SleeveName>{bucket.label}</SleeveName>
                </SleeveNameCell>
                <div style={{ textAlign: 'right' }}>
                  <SleeveTargetInput
                    value={target}
                    onChange={e => {
                      const val = parseFloat(e.target.value) || 0;
                      setSleeveTargets(prev => ({ ...prev, [bucket.key]: val }));
                    }}
                    onClick={e => e.stopPropagation()}
                  />
                </div>
                <SleevePercentValue>{current.toFixed(1)}%</SleevePercentValue>
                <div style={{ textAlign: 'right' }}>
                  <DriftBadge $severity={severity}>
                    {drift >= 0 ? '+' : ''}{drift.toFixed(1)}%
                  </DriftBadge>
                </div>
                <RecommendedAction>
                  {getSleeveAction(bucket, drift)}
                </RecommendedAction>
              </SleeveTableRow>
            );
          })}

          <SleeveTotalRow>
            <SleeveNameCell>
              <SleeveName style={{ color: totalTarget === 100 ? '#10b981' : '#f59e0b' }}>
                Total
              </SleeveName>
            </SleeveNameCell>
            <SleevePercentValue style={{ fontWeight: 700, color: totalTarget === 100 ? '#10b981' : '#f59e0b' }}>
              {totalTarget.toFixed(0)}%
            </SleevePercentValue>
            <SleevePercentValue style={{ fontWeight: 700 }}>
              {totalCurrent.toFixed(1)}%
            </SleevePercentValue>
            <div />
            <div style={{ fontSize: '0.82rem', color: totalTarget !== 100 ? '#f59e0b' : '#64748b' }}>
              {totalTarget !== 100
                ? `Targets sum to ${totalTarget}% (should be 100%)`
                : 'All targets balanced'}
            </div>
          </SleeveTotalRow>
        </SleeveTableContainer>
      </SleeveGrid>

      {/* Commitment summary and cash flow timeline require live IBOR data —
           will be wired when capital call / distribution history APIs are available */}
    </AlternativesContainer>
  );
};

export default MultiPortfolioConstructionScreen;
